// src/email-builder/types.ts
var BLANK_PRODUCT_ID = "__blank__";

// src/email-builder/schema.ts
function getNodeChildren(node) {
  if ("children" in node && Array.isArray(node.children)) {
    return node.children;
  }
  return [];
}
var CANVAS_OBJECT_TYPES = [
  "canvas-shape",
  "canvas-text",
  "canvas-image",
  "canvas-path",
  "canvas-group"
];
var ALLOWED_CHILDREN = {
  email: ["head", "wrapper", "section", "canvas-section", "canvas-shape", "canvas-text", "canvas-image", "canvas-path", "canvas-group"],
  head: [],
  wrapper: ["section", "canvas-section"],
  section: ["column", "group", "product-feed"],
  group: ["column"],
  column: ["text", "image", "button", "divider", "spacer", "social", "navbar"],
  text: [],
  image: [],
  button: [],
  divider: [],
  spacer: [],
  social: [],
  navbar: [],
  "product-feed": [],
  "canvas-section": ["canvas-shape", "canvas-text", "canvas-image", "canvas-path", "canvas-group"],
  "canvas-shape": [],
  "canvas-text": [],
  "canvas-image": [],
  "canvas-path": [],
  "canvas-group": ["canvas-shape", "canvas-text", "canvas-image", "canvas-path", "canvas-group"]
};

// src/email-builder/click-map-bands.ts
var BODY_BAND_ID = "body";
function isRecord(v) {
  return typeof v === "object" && v !== null;
}
function isFiniteNumber(v) {
  return typeof v === "number" && Number.isFinite(v);
}
function normalizeZones(raw, defaultBandId) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const z of raw) {
    if (!isRecord(z)) continue;
    const rect = z.rect;
    if (typeof z.id !== "string" || !isRecord(rect) || !isFiniteNumber(rect.x) || !isFiniteNumber(rect.y) || !isFiniteNumber(rect.width) || !isFiniteNumber(rect.height)) {
      continue;
    }
    out.push({
      ...z,
      id: z.id,
      rect: {
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height
      },
      href: typeof z.href === "string" ? z.href : "",
      alt: typeof z.alt === "string" ? z.alt : "",
      anchorIndex: isFiniteNumber(z.anchorIndex) ? z.anchorIndex : null,
      bandId: typeof z.bandId === "string" && z.bandId ? z.bandId : defaultBandId
    });
  }
  return out;
}
function normalizeClickMapToBands(clickMap) {
  if (!isRecord(clickMap)) return null;
  const utmDefaults = isRecord(clickMap.utmDefaults) ? clickMap.utmDefaults : void 0;
  const rawBands = clickMap.bandBackdrops;
  if (Array.isArray(rawBands) && rawBands.length > 0) {
    const bands = [];
    for (const b of rawBands) {
      if (!isRecord(b)) continue;
      if (typeof b.bandId !== "string" || typeof b.url !== "string" || !isFiniteNumber(b.width) || !isFiniteNumber(b.height)) {
        continue;
      }
      bands.push({
        bandId: b.bandId,
        url: b.url,
        width: b.width,
        height: b.height,
        sceneHash: typeof b.sceneHash === "string" ? b.sceneHash : "",
        kind: b.kind === "live" ? "live" : "image"
      });
    }
    if (bands.length === 0) return null;
    return {
      bands,
      zones: normalizeZones(clickMap.zones, BODY_BAND_ID),
      ...utmDefaults ? { utmDefaults } : {},
      legacy: false
    };
  }
  const backdrop = clickMap.backdrop;
  if (isRecord(backdrop) && typeof backdrop.url === "string" && isFiniteNumber(backdrop.width) && isFiniteNumber(backdrop.height)) {
    return {
      bands: [
        {
          bandId: BODY_BAND_ID,
          url: backdrop.url,
          width: backdrop.width,
          height: backdrop.height,
          sceneHash: "",
          kind: "image"
        }
      ],
      zones: normalizeZones(clickMap.zones, BODY_BAND_ID),
      ...utmDefaults ? { utmDefaults } : {},
      legacy: true
    };
  }
  return null;
}
function bodyBandOf(normalized) {
  if (!normalized || normalized.bands.length !== 1) return null;
  const band = normalized.bands[0];
  return band.bandId === BODY_BAND_ID ? band : null;
}

// src/email-builder/social-icons.ts
var SOCIAL_ICONS = {
  instagram: { platform: "instagram", label: "Instagram", iconifyId: "mdi:instagram" },
  facebook: { platform: "facebook", label: "Facebook", iconifyId: "mdi:facebook" },
  twitter: { platform: "twitter", label: "Twitter", iconifyId: "mdi:twitter" },
  x: { platform: "x", label: "X", iconifyId: "simple-icons:x" },
  tiktok: { platform: "tiktok", label: "TikTok", iconifyId: "ic:baseline-tiktok" },
  youtube: { platform: "youtube", label: "YouTube", iconifyId: "mdi:youtube" },
  linkedin: { platform: "linkedin", label: "LinkedIn", iconifyId: "mdi:linkedin" },
  pinterest: { platform: "pinterest", label: "Pinterest", iconifyId: "mdi:pinterest" },
  threads: { platform: "threads", label: "Threads", iconifyId: "simple-icons:threads" },
  snapchat: { platform: "snapchat", label: "Snapchat", iconifyId: "mdi:snapchat" },
  whatsapp: { platform: "whatsapp", label: "WhatsApp", iconifyId: "mdi:whatsapp" },
  telegram: { platform: "telegram", label: "Telegram", iconifyId: "mdi:telegram" },
  discord: { platform: "discord", label: "Discord", iconifyId: "ic:baseline-discord" },
  reddit: { platform: "reddit", label: "Reddit", iconifyId: "mdi:reddit" }
};
var DEFAULT_CDN_BASE = "https://media.nexmail.ai/static/social";
function socialIconUrl(platform, cdnBase = DEFAULT_CDN_BASE) {
  return `${cdnBase}/${platform}.png`;
}
function isKnownSocialPlatform(value) {
  return value in SOCIAL_ICONS;
}

// src/email-builder/factories.ts
function shortId(prefix) {
  return `${prefix}-${crypto.randomUUID().slice(0, 8)}`;
}
function defaultColumn() {
  return {
    id: shortId("column"),
    type: "column",
    props: {},
    children: []
  };
}
function defaultSection() {
  return {
    id: shortId("section"),
    type: "section",
    props: {},
    style: {
      backgroundColor: "#FFFFFF",
      padding: { top: 20, right: 24, bottom: 20, left: 24 }
    },
    children: [defaultColumn()]
  };
}
function createCanvasObject(type) {
  const id = shortId(type);
  const defaultTransform = { x: 40, y: 40, width: 120, height: 40 };
  switch (type) {
    case "canvas-shape":
      return { id, type, props: { shape: "rectangle", transform: { x: 40, y: 40, width: 120, height: 80 } }, style: { fill: "#6366F1" } };
    case "canvas-text":
      return { id, type, props: { content: "Text", transform: defaultTransform, format: "plain" }, style: { fontSize: 16, color: "#111827" } };
    case "canvas-image":
      return { id, type, props: { src: "", alt: "Image", transform: { x: 40, y: 40, width: 120, height: 120 }, objectFit: "cover" } };
    case "canvas-path":
      return { id, type, props: { d: "M 0 0 L 100 0 L 100 40 Z", transform: { x: 40, y: 40, width: 100, height: 40 } }, style: { fill: "#6366F1" } };
    case "canvas-group":
      return { id, type, props: { transform: { x: 0, y: 0, width: 200, height: 200 } }, children: [] };
  }
}
function createNode(type) {
  const id = shortId(type);
  switch (type) {
    case "section":
      return defaultSection();
    case "wrapper":
      return {
        id,
        type,
        props: { fullWidth: true },
        style: { backgroundColor: "#FFFFFF" },
        children: [defaultSection()]
      };
    case "group":
      return {
        id,
        type,
        props: { preventStackingOnMobile: true },
        children: [defaultColumn()]
      };
    case "column":
      return defaultColumn();
    case "text":
      return {
        id,
        type,
        props: { content: "Start typing here...", format: "plain", role: "body" },
        style: {
          fontSize: 14,
          fontWeight: 400,
          color: "#111827",
          align: "left",
          lineHeight: 1.5,
          padding: { top: 10, right: 10, bottom: 10, left: 10 }
        }
      };
    case "image":
      return {
        id,
        type,
        props: { alt: "Image", src: "https://placehold.co/600x300/e5e7eb/6b7280?text=Image" },
        style: { width: "100%", align: "center" }
      };
    case "button":
      return {
        id,
        type,
        props: { label: "Shop Now", href: "https://example.com", variant: "primary" },
        style: {
          backgroundColor: "#111827",
          color: "#FFFFFF",
          borderRadius: 6,
          fontSize: 14,
          fontWeight: 600,
          padding: { top: 12, right: 24, bottom: 12, left: 24 },
          align: "center"
        }
      };
    case "divider":
      return {
        id,
        type,
        props: {},
        style: {
          color: "#E5E7EB",
          thickness: 2,
          width: "100%",
          padding: { top: 10, right: 0, bottom: 10, left: 0 }
        }
      };
    case "spacer":
      return { id, type, props: { height: 24 } };
    case "social":
      return {
        id,
        type,
        props: {
          links: [
            { platform: "instagram", href: "https://instagram.com" },
            { platform: "twitter", href: "https://twitter.com" }
          ],
          iconStyle: "filled"
        },
        style: { iconSize: 24, align: "center", padding: 10 }
      };
    case "navbar":
      return {
        id,
        type,
        props: {
          links: [
            { label: "Shop", href: "https://example.com" },
            { label: "About", href: "https://example.com" }
          ]
        },
        style: { fontSize: 14, align: "center", padding: 10 }
      };
    case "product-feed":
      return {
        id,
        type,
        props: {
          source: { kind: "recommended" },
          layout: "stack",
          limit: 1,
          fallback: "placeholder",
          itemTemplate: {
            include: ["image", "title", "price", "cta"],
            ctaLabel: "Shop Now",
            ctaVariant: "primary"
          }
        }
      };
    case "canvas-section":
      return {
        id,
        type,
        props: {
          scene: { width: 600, height: 240, backgroundColor: "#F3F4F6" },
          alt: "Canvas section",
          fallbackText: "Canvas content"
        },
        children: []
      };
    case "canvas-shape":
      return { id, type, props: { shape: "rectangle", transform: { x: 0, y: 0, width: 600, height: 240 } }, style: { fill: "#6366F1" } };
    case "canvas-text":
      return { id, type, props: { content: "Text", transform: { x: 0, y: 0, width: 600, height: 60 }, format: "plain" }, style: { fontSize: 24, color: "#111827" } };
    case "canvas-image":
      return { id, type, props: { src: "", alt: "Image", transform: { x: 0, y: 0, width: 600, height: 300 }, objectFit: "cover" } };
    case "canvas-path":
      return { id, type, props: { d: "M 0 0 L 600 0 L 600 240 Z", transform: { x: 0, y: 0, width: 600, height: 240 } }, style: { fill: "#6366F1" } };
    case "canvas-group":
      return { id, type, props: { transform: { x: 0, y: 0, width: 600, height: 240 } }, children: [] };
    case "head":
      return { id, type, props: { title: "Untitled Email", previewText: "" } };
    case "email":
      throw new Error("Use createEmptyEmailDocument() to create email roots.");
  }
}
var SECTION_PRESETS = [
  { key: "section-1", label: "Section \xB7 1 column", columns: 1, group: false },
  { key: "section-2", label: "Section \xB7 2 columns", columns: 2, group: false },
  { key: "section-2-group", label: "Section \xB7 2 cols (side-by-side)", columns: 2, group: true },
  { key: "section-3-group", label: "Section \xB7 3 cols (side-by-side)", columns: 3, group: true },
  { key: "section-4-group", label: "Section \xB7 4 cols (side-by-side)", columns: 4, group: true }
];
function createSectionSubtree(preset) {
  const columns = Array.from({ length: preset.columns }, () => defaultColumn());
  const section = defaultSection();
  if (preset.group) {
    section.children = [
      {
        id: shortId("group"),
        type: "group",
        props: { preventStackingOnMobile: true },
        children: columns
      }
    ];
  } else {
    section.children = columns;
  }
  return section;
}
var KNOWN_SOCIAL_PLATFORMS = new Set(Object.keys(SOCIAL_ICONS));
function createHeaderSubtree(brand) {
  const siteUrl = brand?.websiteUrl ?? "https://example.com";
  const allLinks = brand?.navLinks?.length ? brand.navLinks.map((l) => ({ label: l.title, href: l.url })) : [
    { label: "Shop", href: siteUrl },
    { label: "About", href: siteUrl },
    { label: "Collections", href: siteUrl },
    { label: "Sale", href: siteUrl }
  ];
  const half = Math.ceil(allLinks.length / 2);
  const leftLinks = allLinks.slice(0, half);
  const rightLinks = allLinks.slice(half).length ? allLinks.slice(half) : [{ label: "Collections", href: siteUrl }, { label: "Sale", href: siteUrl }];
  const leftCol = defaultColumn();
  leftCol.props = { width: "33%", verticalAlign: "middle" };
  leftCol.children = [
    {
      id: shortId("navbar"),
      type: "navbar",
      props: { links: leftLinks },
      style: { align: "left", fontSize: 13, color: "#374151" }
    }
  ];
  const logoCol = defaultColumn();
  logoCol.props = { width: "34%", verticalAlign: "middle" };
  logoCol.children = [
    {
      id: shortId("image"),
      type: "image",
      props: { src: { $brandAsset: "logo" }, alt: "Logo", href: siteUrl, variant: "logo" },
      style: { width: 120, align: "center" }
    }
  ];
  const rightCol = defaultColumn();
  rightCol.props = { width: "33%", verticalAlign: "middle" };
  rightCol.children = [
    {
      id: shortId("navbar"),
      type: "navbar",
      props: { links: rightLinks },
      style: { align: "right", fontSize: 13, color: "#374151" }
    }
  ];
  const section = defaultSection();
  section.name = "Header";
  section.meta = { aiHints: { role: "header" } };
  section.style = {
    backgroundColor: "#FFFFFF",
    padding: { top: 12, right: 24, bottom: 12, left: 24 },
    borderBottom: "1px solid #E5E7EB"
  };
  section.children = [
    {
      id: shortId("group"),
      type: "group",
      props: { preventStackingOnMobile: true },
      children: [leftCol, logoCol, rightCol]
    }
  ];
  return section;
}
function createFooterSubtree(brand) {
  const socialLinks = brand?.socialLinks ? Object.entries(brand.socialLinks).filter(([platform, href]) => KNOWN_SOCIAL_PLATFORMS.has(platform) && href).map(([platform, href]) => ({ platform, href })) : [];
  const col = defaultColumn();
  col.children = [
    {
      id: shortId("social"),
      type: "social",
      props: { links: socialLinks, iconStyle: "outline" },
      style: { align: "center", padding: { top: 0, right: 0, bottom: 12, left: 0 } }
    },
    {
      id: shortId("text"),
      type: "text",
      props: {
        content: '<a href="[NM:UNSUBSCRIBE_URL]" style="color:#9CA3AF;">Unsubscribe</a> &nbsp;\xB7&nbsp; <a href="[NM:PRIVACY_URL]" style="color:#9CA3AF;">Privacy Policy</a>',
        format: "html",
        role: "legal"
      },
      style: { align: "center", fontSize: 11, color: "#9CA3AF", padding: { top: 0, right: 0, bottom: 0, left: 0 } }
    },
    {
      id: shortId("text"),
      type: "text",
      props: { content: "[NM:COMPANY_ADDRESS]", format: "plain", role: "legal" },
      style: { align: "center", fontSize: 10, color: "#9CA3AF", padding: { top: 4, right: 0, bottom: 0, left: 0 } }
    }
  ];
  const section = defaultSection();
  section.name = "Footer";
  section.meta = { aiHints: { role: "footer" } };
  section.style = { backgroundColor: "#F9FAFB", padding: { top: 24, right: 24, bottom: 24, left: 24 } };
  section.children = [col];
  return section;
}
function createEmptyEmailDocument(authorId) {
  const now = (/* @__PURE__ */ new Date()).toISOString();
  return {
    schemaVersion: 4,
    meta: { createdAt: now, updatedAt: now, authorId, version: 0 },
    root: {
      id: "email-root",
      type: "email",
      props: { width: 600, darkModeStrategy: "none" },
      style: { backgroundColor: "#F3F4F6", textColor: "#111827", fontFamily: "Helvetica, Arial, sans-serif" },
      children: [
        { id: "head-root", type: "head", props: { title: "Untitled Email", previewText: "" } }
      ]
    }
  };
}
function walkTree(node, visit, parent = null) {
  visit(node, parent);
  for (const child of node.children ?? []) {
    walkTree(child, visit, node);
  }
}
function findNode(document, nodeId) {
  function search(node) {
    if (node.id === nodeId) return node;
    for (const child of node.children ?? []) {
      const found = search(child);
      if (found) return found;
    }
    return null;
  }
  return search(document.root);
}
function findParent(document, nodeId) {
  function searchParent(node) {
    const children = node.children ?? [];
    for (const child of children) {
      if (child.id === nodeId) return node;
      const found = searchParent(child);
      if (found) return found;
    }
    return null;
  }
  return searchParent(document.root);
}
function isContainerNode(node) {
  const children = node.children;
  return Array.isArray(children);
}
function getBodyChildren(document) {
  return document.root.children.slice(1);
}

// src/email-builder/merge-tag-vocabulary.ts
var MergeTag = /* @__PURE__ */ ((MergeTag2) => {
  MergeTag2["UNSUBSCRIBE_URL"] = "UNSUBSCRIBE_URL";
  MergeTag2["VIEW_IN_BROWSER_URL"] = "VIEW_IN_BROWSER_URL";
  MergeTag2["PRIVACY_URL"] = "PRIVACY_URL";
  MergeTag2["MANAGE_PREFERENCES_URL"] = "MANAGE_PREFERENCES_URL";
  MergeTag2["COMPANY_ADDRESS"] = "COMPANY_ADDRESS";
  MergeTag2["COMPANY_NAME"] = "COMPANY_NAME";
  MergeTag2["TERMS_URL"] = "TERMS_URL";
  MergeTag2["CONTACT_URL"] = "CONTACT_URL";
  MergeTag2["COOKIE_URL"] = "COOKIE_URL";
  MergeTag2["FAQ_URL"] = "FAQ_URL";
  MergeTag2["SHIPPING_URL"] = "SHIPPING_URL";
  MergeTag2["FIRST_NAME"] = "FIRST_NAME";
  MergeTag2["LAST_NAME"] = "LAST_NAME";
  MergeTag2["EMAIL"] = "EMAIL";
  MergeTag2["FULL_NAME"] = "FULL_NAME";
  return MergeTag2;
})(MergeTag || {});
function nmToken(tag) {
  return `[NM:${tag}]`;
}
var MERGE_TAG_CATEGORIES = [
  // Compliance tags
  {
    id: "UNSUBSCRIBE_URL" /* UNSUBSCRIBE_URL */,
    label: "Unsubscribe URL",
    description: "Link for recipients to unsubscribe from emails",
    category: "compliance",
    placeholder: nmToken("UNSUBSCRIBE_URL" /* UNSUBSCRIBE_URL */)
  },
  {
    id: "VIEW_IN_BROWSER_URL" /* VIEW_IN_BROWSER_URL */,
    label: "View in Browser URL",
    description: "Link to view the email in a web browser",
    category: "compliance",
    placeholder: nmToken("VIEW_IN_BROWSER_URL" /* VIEW_IN_BROWSER_URL */)
  },
  {
    id: "PRIVACY_URL" /* PRIVACY_URL */,
    label: "Privacy Policy URL",
    description: "Link to your privacy policy page",
    category: "compliance",
    placeholder: nmToken("PRIVACY_URL" /* PRIVACY_URL */)
  },
  {
    id: "MANAGE_PREFERENCES_URL" /* MANAGE_PREFERENCES_URL */,
    label: "Manage Preferences URL",
    description: "Link for recipients to manage email preferences",
    category: "compliance",
    placeholder: nmToken("MANAGE_PREFERENCES_URL" /* MANAGE_PREFERENCES_URL */)
  },
  {
    id: "COMPANY_ADDRESS" /* COMPANY_ADDRESS */,
    label: "Company Address",
    description: "Your company mailing address (required for CAN-SPAM)",
    category: "compliance",
    placeholder: nmToken("COMPANY_ADDRESS" /* COMPANY_ADDRESS */)
  },
  {
    id: "COMPANY_NAME" /* COMPANY_NAME */,
    label: "Company Name",
    description: "Your company or brand name",
    category: "compliance",
    placeholder: nmToken("COMPANY_NAME" /* COMPANY_NAME */)
  },
  // Brand-specific URL tags — no ESP ships native helpers for these, so
  // they fall back to `#` in every per-provider registry. Authors override
  // per-insert via the editor, or the brand profile will eventually carry these
  // fields and the compile-time resolver will fill them in.
  {
    id: "TERMS_URL" /* TERMS_URL */,
    label: "Terms & Conditions URL",
    description: "Link to your terms of service / terms and conditions page",
    category: "brand-links",
    placeholder: nmToken("TERMS_URL" /* TERMS_URL */)
  },
  {
    id: "CONTACT_URL" /* CONTACT_URL */,
    label: "Contact URL",
    description: "Link to your contact / support page",
    category: "brand-links",
    placeholder: nmToken("CONTACT_URL" /* CONTACT_URL */)
  },
  {
    id: "COOKIE_URL" /* COOKIE_URL */,
    label: "Cookie Policy URL",
    description: "Link to your cookie policy page",
    category: "brand-links",
    placeholder: nmToken("COOKIE_URL" /* COOKIE_URL */)
  },
  {
    id: "FAQ_URL" /* FAQ_URL */,
    label: "FAQ URL",
    description: "Link to your frequently asked questions page",
    category: "brand-links",
    placeholder: nmToken("FAQ_URL" /* FAQ_URL */)
  },
  {
    id: "SHIPPING_URL" /* SHIPPING_URL */,
    label: "Shipping & Returns URL",
    description: "Link to your shipping and returns policy page",
    category: "brand-links",
    placeholder: nmToken("SHIPPING_URL" /* SHIPPING_URL */)
  },
  // Personalization tags
  {
    id: "FIRST_NAME" /* FIRST_NAME */,
    label: "First Name",
    description: "Recipient first name",
    category: "personalization",
    placeholder: nmToken("FIRST_NAME" /* FIRST_NAME */)
  },
  {
    id: "LAST_NAME" /* LAST_NAME */,
    label: "Last Name",
    description: "Recipient last name",
    category: "personalization",
    placeholder: nmToken("LAST_NAME" /* LAST_NAME */)
  },
  {
    id: "EMAIL" /* EMAIL */,
    label: "Email Address",
    description: "Recipient email address",
    category: "personalization",
    placeholder: nmToken("EMAIL" /* EMAIL */)
  },
  {
    id: "FULL_NAME" /* FULL_NAME */,
    label: "Full Name",
    description: "Recipient full name",
    category: "personalization",
    placeholder: nmToken("FULL_NAME" /* FULL_NAME */)
  }
];

// src/email-builder/merge-tags.ts
var BRAND_MERGE_TAG_PATHS = [
  // Identity
  "$brand.name",
  "$brand.description",
  "$brand.tagline",
  "$brand.slogan",
  "$brand.website",
  "$brand.email",
  "$brand.address",
  "$brand.industry",
  // Imagery
  "$brand.logoUrl",
  // Styling scalars
  "$brand.borderRadius",
  // Typography (semantic — resolved by usage)
  "$brand.font.heading",
  "$brand.font.body",
  // Color tokens (semantic — resolved by usage). See BrandColorDtoUsage for
  // the full set of allowed usage values; these are the common ones.
  "$brand.color.primary",
  "$brand.color.secondary",
  "$brand.color.accent",
  "$brand.color.background",
  "$brand.color.emailBackground",
  "$brand.color.surface",
  "$brand.color.text",
  "$brand.color.button",
  "$brand.color.buttonSecondary",
  "$brand.color.link",
  "$brand.color.border",
  // Social (structured — social.<platform>). Kept aligned with SocialPlatform
  // in social-icons.ts so any platform the editor can pick has a tag.
  "$brand.social.instagram",
  "$brand.social.facebook",
  "$brand.social.twitter",
  "$brand.social.x",
  "$brand.social.tiktok",
  "$brand.social.youtube",
  "$brand.social.linkedin",
  "$brand.social.pinterest",
  "$brand.social.threads",
  "$brand.social.snapchat",
  "$brand.social.whatsapp",
  "$brand.social.telegram",
  "$brand.social.discord",
  "$brand.social.reddit",
  // Indexed collection refs — `$brand.collections[N].title|url|imageUrl`.
  // Resolves against the active client's connected-store collections (Shopify,
  // etc.) ordered by sortOrder / productsCount. Documented as `[0]` for type
  // surfacing; the regex accepts any index. Per-field fallback when the slot is
  // missing: `.title` → "Shop", `.url` → `$brand.website`, `.imageUrl` → unresolved.
  "$brand.collections[0].title",
  "$brand.collections[0].url",
  "$brand.collections[0].imageUrl"
];
var BRAND_TAG_REGEX = /\$brand\.[a-zA-Z][a-zA-Z0-9_]*(?:\[\d+\])?(?:\.[a-zA-Z][a-zA-Z0-9_]*)?/g;
var PRODUCT_TAG_REGEX = /\$product\[\d+\]\.[a-zA-Z][a-zA-Z0-9_]*/g;
var SLOT_TAG_REGEX = /\$slot\.[a-zA-Z][a-zA-Z0-9_-]*(?:\.[a-zA-Z][a-zA-Z0-9_-]*)?/g;
var NM_TOKEN_REGEX = /\[NM:[^\]]*\]/g;
var NM_LEGACY_BODY = /^[A-Z][A-Z0-9_]*$/;
var NM_ESP_BODY = /^ESP\.([A-Z][A-Z0-9_]*)\.([A-Za-z][A-Za-z0-9_]*(?:\.[A-Za-z][A-Za-z0-9_]*)*)$/;
var RECIPIENT_PROPERTY_INNER = "[^\\]|]";
var RECIPIENT_PROPERTY_EDGE = "[^\\]|\\s]";
var RECIPIENT_PROPERTY = `${RECIPIENT_PROPERTY_EDGE}(?:${RECIPIENT_PROPERTY_INNER}*${RECIPIENT_PROPERTY_EDGE})?`;
var RECIPIENT_PROPERTY_REGEX = new RegExp(`^${RECIPIENT_PROPERTY}$`);
var NM_FIELD_BODY = new RegExp(
  `^FIELD\\.recipient\\.(${RECIPIENT_PROPERTY})(?:\\|([^\\]|]*))?$`
);
var AUTO_CONTRAST_REGEX = /\$auto\.contrast\(([^)]+)\)/g;
var AUTO_HEX_NO_HASH_REGEX = /\$auto\.hexNoHash\(([^)]+)\)/g;
var AUTO_ALPHA_REGEX = /\$auto\.alpha\(([^)]+)\)/g;
var AUTO_CURRENT_YEAR_REGEX = /\$auto\.currentYear\b/g;
var HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;
function isBrandMergeTag(tag) {
  return /^\$brand\.[a-zA-Z][a-zA-Z0-9_]*(?:\[\d+\])?(?:\.[a-zA-Z][a-zA-Z0-9_]*)?$/.test(tag);
}
function isSlotMergeTag(tag) {
  return /^\$slot\.[a-zA-Z][a-zA-Z0-9_-]*(?:\.[a-zA-Z][a-zA-Z0-9_-]*)?$/.test(tag);
}
function findNmTags(s) {
  const out = [];
  const scan = new RegExp(NM_TOKEN_REGEX.source, "g");
  let m;
  while (m = scan.exec(s)) out.push({ token: m[0], index: m.index });
  return out;
}
function classifyNmTag(token) {
  const outer = /^\[NM:([^\]]*)\]$/.exec(token);
  if (!outer) return { kind: "malformed", namespace: null, reason: "not a [NM:...] token" };
  const body = outer[1];
  if (body.startsWith("FIELD.")) {
    const field = NM_FIELD_BODY.exec(body);
    if (!field) {
      return {
        kind: "malformed",
        namespace: "field",
        reason: "expected [NM:FIELD.recipient.<property>] with an optional |<fallback>, where <property> is non-empty, neither starts nor ends with whitespace, and neither part holds a ] or a |"
      };
    }
    return { kind: "field", property: field[1], fallback: field[2] ?? null };
  }
  if (body.startsWith("ESP.")) {
    const esp = NM_ESP_BODY.exec(body);
    if (!esp) {
      return {
        kind: "malformed",
        namespace: "esp",
        reason: "expected [NM:ESP.<PROVIDER>.<dotted.path>] with an upper-snake provider and a dotted alphanumeric path"
      };
    }
    return { kind: "esp", provider: esp[1], path: esp[2] };
  }
  if (NM_LEGACY_BODY.test(body)) return { kind: "legacy", name: body };
  return {
    kind: "malformed",
    namespace: null,
    reason: body === "" ? "the tag has no name" : "not one of the [NM:*] shapes"
  };
}
function isSendTimeMergeTag(tag) {
  return classifyNmTag(tag).kind !== "malformed";
}
function parseRecipientFieldTag(s) {
  const tag = classifyNmTag(s);
  return tag.kind === "field" ? { property: tag.property, fallback: tag.fallback } : null;
}
function isRecipientFieldProperty(property) {
  return RECIPIENT_PROPERTY_REGEX.test(property);
}
var TEMPLATE_DELIMITERS = /\{\{|\}\}|\{%|%\}/;
function containsTemplateDelimiters(text) {
  return TEMPLATE_DELIMITERS.test(text);
}
function hasMergeTag(s) {
  return BRAND_TAG_REGEX.test(s) || PRODUCT_TAG_REGEX.test(s) || SLOT_TAG_REGEX.test(s) || findNmTags(s).some((t) => isSendTimeMergeTag(t.token));
}
function parseSlotTag(s) {
  const m = /^\$slot\.([a-zA-Z][a-zA-Z0-9_-]*(?:\.[a-zA-Z][a-zA-Z0-9_-]*)?)$/.exec(s);
  return m ? m[1] : null;
}
function relativeLuminance(hex) {
  const normalized = hex.trim().replace(/^#/, "");
  const full = normalized.length === 3 ? normalized.split("").map((c) => c + c).join("") : normalized.length === 8 ? normalized.slice(0, 6) : normalized;
  if (full.length !== 6 || !/^[0-9a-f]{6}$/i.test(full)) return null;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const channel = (c) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
function hexToRgba(color, alpha) {
  const a = Math.max(0, Math.min(1, alpha));
  const hex = color.trim().replace(/^#/, "");
  const full = hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex.length === 8 ? hex.slice(0, 6) : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) return color;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}
function darkenHex(hex, amount) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return "#F5F5F5";
  const v = parseInt(m[1], 16);
  const r = Math.max(0, Math.round((v >> 16 & 255) * (1 - amount)));
  const g = Math.max(0, Math.round((v >> 8 & 255) * (1 - amount)));
  const b = Math.max(0, Math.round((v & 255) * (1 - amount)));
  return "#" + [r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("").toUpperCase();
}
function contrastingText(bgHex) {
  const lum = relativeLuminance(bgHex);
  if (lum == null) return "#111111";
  const vsWhite = 1.05 / (lum + 0.05);
  const vsBlack = (lum + 0.05) / 0.05;
  return vsWhite > vsBlack ? "#FFFFFF" : "#111111";
}
function resolveMergeTags(value, brand, resolveSlot) {
  if (!value) return value;
  let out = value;
  out = out.replace(BRAND_TAG_REGEX, (match) => {
    const resolved = resolveBrandPath(match, brand);
    return resolved ?? match;
  });
  out = out.replace(PRODUCT_TAG_REGEX, (match) => {
    const resolved = resolveProductPath(match, brand);
    return resolved ?? match;
  });
  if (resolveSlot) {
    out = out.replace(SLOT_TAG_REGEX, (match) => {
      const name = parseSlotTag(match);
      if (!name) return match;
      const resolved = resolveSlot(name);
      return resolved ?? match;
    });
  }
  const recipient = brand?.recipient;
  if (recipient) {
    out = out.replace(NM_TOKEN_REGEX, (token) => {
      const tag = classifyNmTag(token);
      if (tag.kind !== "field") return token;
      const value2 = recipient[tag.property];
      return value2 ? value2 : tag.fallback ?? "";
    });
  }
  out = out.replace(AUTO_CONTRAST_REGEX, (match, inner) => {
    const bg = inner.trim();
    if (HEX_COLOR_REGEX.test(bg)) return contrastingText(bg);
    return match;
  });
  out = out.replace(AUTO_HEX_NO_HASH_REGEX, (match, inner) => {
    const value2 = inner.trim();
    if (HEX_COLOR_REGEX.test(value2)) return value2.replace(/^#/, "");
    return match;
  });
  out = out.replace(AUTO_ALPHA_REGEX, (match, inner) => {
    const parts = inner.split(",");
    if (parts.length !== 2) return match;
    const color = parts[0].trim();
    const alpha = parseFloat(parts[1].trim());
    if (!HEX_COLOR_REGEX.test(color) || !Number.isFinite(alpha)) return match;
    return hexToRgba(color, alpha);
  });
  out = out.replace(
    AUTO_CURRENT_YEAR_REGEX,
    () => String((/* @__PURE__ */ new Date()).getFullYear())
  );
  return out;
}
function parseIndexedKey(key) {
  const m = /^([a-zA-Z][a-zA-Z0-9_]*)\[(\d+)\]$/.exec(key);
  if (!m) return null;
  return { key: m[1], index: parseInt(m[2], 10) };
}
function resolveBrandPath(path, brand) {
  if (!brand) return void 0;
  const parts = path.split(".");
  if (parts.length < 2 || parts[0] !== "$brand") return void 0;
  const [, key, sub] = parts;
  const indexed = parseIndexedKey(key);
  if (indexed && sub) {
    if (indexed.key === "collections") {
      const cols = brand.collections;
      const item = cols && cols.length > 0 ? cols[indexed.index % cols.length] : void 0;
      switch (sub) {
        case "title":
          return item?.title ?? "Shop";
        case "url":
          return item?.url ?? brand.website;
        case "imageUrl":
          return item?.imageUrl ?? cols?.find((c) => !!c.imageUrl)?.imageUrl ?? brand.products?.find((p) => !!p.imageUrl)?.imageUrl;
        default:
          return void 0;
      }
    }
    return void 0;
  }
  if (!sub) {
    switch (key) {
      case "name":
        return brand.name;
      case "description":
        return brand.description;
      case "tagline":
        return brand.tagline;
      case "slogan":
        return brand.slogan;
      case "website":
        return brand.website;
      case "email":
        return brand.email;
      case "address":
        return brand.address;
      case "industry":
        return brand.industry;
      case "logoUrl":
        return brand.logoUrl;
      case "borderRadius":
        return brand.borderRadius;
      default:
        return void 0;
    }
  }
  switch (key) {
    case "color":
      if (sub === "emailBackground") {
        return brand.colorByUsage?.emailBackground ?? "#FFFFFF";
      }
      if (sub === "surface") {
        return brand.colorByUsage?.surface ?? darkenHex(brand.colorByUsage?.background ?? "#FFFFFF", 0.04);
      }
      return brand.colorByUsage?.[sub];
    case "font":
      return brand.fontByUsage?.[sub];
    case "social":
      return brand.socialByPlatform?.[sub] ?? brand.website;
    default:
      return void 0;
  }
}
function resolveProductPath(path, brand) {
  const m = /^\$product\[(\d+)\]\.([a-zA-Z][a-zA-Z0-9_]*)$/.exec(path);
  if (!m) return void 0;
  const idx = parseInt(m[1], 10);
  const field = m[2];
  const products = brand?.products;
  const product = products && products.length > 0 ? products[idx % products.length] : void 0;
  if (!product) {
    if (field === "url") return brand?.website;
    return void 0;
  }
  switch (field) {
    case "url":
      return product.productUrl ?? brand?.website;
    case "name":
    // Alias: the email-design specialist sometimes authors $product[N].title
    // even though `.name` is canonical. Treat them as equivalent so a single
    // wrong-field reach doesn't leak literal merge tags into rendered output.
    case "title":
      return product.name;
    case "imageUrl":
      return product.imageUrl ?? products?.find((p) => !!p.imageUrl)?.imageUrl ?? brand?.collections?.find((c) => !!c.imageUrl)?.imageUrl;
    case "price":
      return product.price;
    default:
      return void 0;
  }
}
function resolveMergeTagsInTree(subtree, brand, resolveSlot) {
  walkStrings(subtree, (s) => resolveMergeTags(s, brand, resolveSlot));
}
function walkStrings(node, transform) {
  if (node == null) return;
  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i++) {
      const item = node[i];
      if (typeof item === "string") {
        node[i] = transform(item);
      } else if (item && typeof item === "object") {
        walkStrings(item, transform);
      }
    }
    return;
  }
  if (typeof node === "object") {
    const obj = node;
    for (const k of Object.keys(obj)) {
      const v = obj[k];
      if (typeof v === "string") {
        obj[k] = transform(v);
      } else if (v && typeof v === "object") {
        walkStrings(v, transform);
      }
    }
  }
}

// src/email-builder/section-resolver.ts
function deepCloneWithNewIds(node) {
  const clone = structuredClone(node);
  reassignIds(clone);
  return clone;
}
function reassignIds(node) {
  if (!node || typeof node !== "object") return;
  node.id = `${node.type}-${crypto.randomUUID().slice(0, 8)}`;
  if ("children" in node && Array.isArray(node.children)) {
    for (const child of node.children) reassignIds(child);
  }
}
function mediaOffsetForId(id) {
  if (!id) return 0;
  let h = 5381;
  for (let i = 0; i < id.length; i++) h = (h << 5) + h + id.charCodeAt(i) | 0;
  return Math.abs(h);
}
var stockImageForSlot = (slotName) => `https://picsum.photos/seed/${encodeURIComponent(slotName)}/800/600`;
function resolveSectionForInsert(args) {
  const cloned = deepCloneWithNewIds(args.node);
  const usedMedia = args.usedMediaUrls ?? /* @__PURE__ */ new Set();
  const usedProducts = args.usedProductIds ?? /* @__PURE__ */ new Set();
  const cutoutUrlBySlot = /* @__PURE__ */ new Map();
  stampProductCutoutIds(cloned, args, usedMedia, cutoutUrlBySlot);
  const productBySlot = /* @__PURE__ */ new Map();
  const slotResolver = (name) => {
    const dotIdx = name.indexOf(".");
    if (dotIdx > 0) {
      const baseName = name.slice(0, dotIdx);
      const field = name.slice(dotIdx + 1);
      const baseDef = args.slotDefaults[baseName];
      if (!baseDef || baseDef.kind !== "product") return void 0;
      let picked = productBySlot.get(baseName);
      if (!picked && args.pickProductRich) {
        const fresh = args.pickProductRich(baseName, usedProducts);
        if (fresh) {
          usedProducts.add(fresh.variantId);
          productBySlot.set(baseName, fresh);
          picked = fresh;
        }
      }
      if (!picked) return void 0;
      switch (field) {
        case "id":
        case "variantId":
          return picked.variantId;
        case "productId":
          return picked.productId;
        case "name":
        case "title":
          return picked.name;
        case "price":
          return picked.price ?? "";
        case "compareAtPrice":
          return picked.compareAtPrice ?? "";
        case "cutoutImage":
          return picked.transparentImageUrl ?? picked.fallbackImageUrl;
        case "image":
          return picked.fallbackImageUrl;
        case "url":
        case "productUrl":
          return picked.productUrl;
        default:
          return void 0;
      }
    }
    const def = args.slotDefaults[name];
    const schema = args.slots[name];
    if (!def) return void 0;
    if (def.kind === "text" || def.kind === "rich-text") {
      return def.value;
    }
    if (def.kind === "image") {
      const fromCutout = cutoutUrlBySlot.get(name);
      if (fromCutout) return fromCutout;
      if (def.strategy === "transparent-product" && args.pickTransparentProductMedia) {
        const picked = args.pickTransparentProductMedia(name, usedMedia);
        if (picked) {
          usedMedia.add(picked.url);
          return picked.url;
        }
      }
      if (def.strategy === "recent-media" && args.pickRecentMedia) {
        const picked = args.pickRecentMedia(name, usedMedia, args.mediaOffset);
        if (picked) {
          usedMedia.add(picked);
          return picked;
        }
      }
      if (def.strategy === "placeholder" && def.placeholder) {
        return def.placeholder;
      }
      return stockImageForSlot(name);
    }
    if (def.kind === "product") {
      const cached = productBySlot.get(name);
      if (cached) return cached.variantId;
      if (args.pickProduct) {
        const picked = args.pickProduct(name, usedProducts);
        if (picked) {
          usedProducts.add(picked);
          return picked;
        }
      }
      return "__blank__";
    }
    if (def.kind === "link") {
      if (def.strategy === "brand-homepage") {
        return args.brand?.website ?? "";
      }
      return "";
    }
    void schema;
    return void 0;
  };
  resolveMergeTagsInTree(cloned, args.brand, slotResolver);
  resolveNavLinksInTree(cloned, args.slots, args.slotDefaults, args.brand);
  return cloned;
}
var SLOT_REF_RE = /^\$slot\.([a-zA-Z][a-zA-Z0-9_]*)$/;
function stampProductCutoutIds(node, args, usedMedia, out) {
  if (node == null) return;
  if (Array.isArray(node)) {
    for (const item of node) stampProductCutoutIds(item, args, usedMedia, out);
    return;
  }
  if (typeof node !== "object") return;
  const obj = node;
  if (obj.type === "image" && obj.props && typeof obj.props === "object") {
    const props = obj.props;
    if (props.productCutout === true) {
      const src = typeof props.src === "string" ? props.src : void 0;
      const match = src ? SLOT_REF_RE.exec(src) : null;
      const slotName = match ? match[1] : void 0;
      let url;
      let productId;
      if (slotName && out.has(slotName)) {
        url = out.get(slotName);
      } else {
        const pickerSlot = slotName ?? "__product_cutout__";
        if (args.pickTransparentProductMedia) {
          const picked = args.pickTransparentProductMedia(pickerSlot, usedMedia);
          if (picked) {
            url = picked.url;
            productId = picked.productId;
          }
        }
        if (!url && args.pickRecentMedia) {
          url = args.pickRecentMedia(pickerSlot, usedMedia, args.mediaOffset);
        }
        if (url) {
          usedMedia.add(url);
          if (slotName) out.set(slotName, url);
        }
      }
      if (productId) props.productId = productId;
      delete props.productCutout;
    }
  }
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v && typeof v === "object") stampProductCutoutIds(v, args, usedMedia, out);
  }
}
function resolveNavLinksInTree(node, slots, slotDefaults, brand) {
  if (node == null) return;
  if (Array.isArray(node)) {
    for (const item of node) resolveNavLinksInTree(item, slots, slotDefaults, brand);
    return;
  }
  if (typeof node !== "object") return;
  const obj = node;
  if (obj.type === "navbar" && obj.props && typeof obj.props === "object") {
    const props = obj.props;
    const linksSlot = typeof props.linksSlot === "string" ? props.linksSlot : void 0;
    if (linksSlot) {
      const schema = slots[linksSlot];
      const def = slotDefaults[linksSlot];
      if (schema?.kind === "nav-links" && def?.kind === "nav-links") {
        const resolved = pickNavLinks(def, brand);
        if (resolved && resolved.length > 0) {
          const offset = typeof props.linksOffset === "number" ? props.linksOffset : 0;
          const limit = typeof props.linksLimit === "number" ? props.linksLimit : void 0;
          const sliced = limit == null ? resolved.slice(offset) : resolved.slice(offset, offset + limit);
          if (sliced.length > 0) props.links = sliced;
        }
        delete props.linksSlot;
        delete props.linksOffset;
        delete props.linksLimit;
      }
    }
  }
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v && typeof v === "object") resolveNavLinksInTree(v, slots, slotDefaults, brand);
  }
}
function pickNavLinks(def, brand) {
  if (def.strategy === "brand-nav-links") {
    const fromBrand = brand?.navLinks;
    if (fromBrand && fromBrand.length > 0) return fromBrand;
    if (def.fallback && def.fallback.length > 0) return def.fallback;
    return void 0;
  }
  return void 0;
}

// src/email-builder/compiler/util/mjml-node.ts
function mjmlNodeToString(node) {
  const attrStr = Object.entries(node.attributes ?? {}).map(([k, v]) => `${k}="${escapeHtml(String(v))}"`).join(" ");
  const open = attrStr ? `<${node.tagName} ${attrStr}>` : `<${node.tagName}>`;
  if (node.content !== void 0) {
    return `${open}${node.content}</${node.tagName}>`;
  }
  if (node.children?.length) {
    return `${open}${node.children.map(mjmlNodeToString).join("")}</${node.tagName}>`;
  }
  return attrStr ? `<${node.tagName} ${attrStr} />` : `<${node.tagName} />`;
}
function compact(obj) {
  const result = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== void 0 && v !== null && v !== "") result[k] = v;
  }
  return result;
}
function px(value) {
  if (value === void 0 || value === null || value === "") return void 0;
  return typeof value === "number" ? `${value}px` : String(value);
}
function lineHeight(value) {
  if (value === void 0 || value === null || value === "") return void 0;
  if (typeof value === "number") {
    return value < 4 ? String(value) : `${value}px`;
  }
  return String(value);
}
function spacing(value) {
  if (value === void 0) return void 0;
  if (typeof value === "number") return `${value}px`;
  return `${value.top ?? 0}px ${value.right ?? 0}px ${value.bottom ?? 0}px ${value.left ?? 0}px`;
}
function borderRadius(value) {
  if (value === void 0 || value === null || value === "") return void 0;
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    const v = value;
    const corner = (x) => x === void 0 || x === null || x === "" ? "0" : typeof x === "number" ? `${x}px` : String(x);
    return `${corner(v.tl)} ${corner(v.tr)} ${corner(v.br)} ${corner(v.bl)}`;
  }
  return typeof value === "number" ? `${value}px` : String(value);
}
function borderStyleToCss(value) {
  if (!value || !value.color) return void 0;
  const width = typeof value.width === "number" ? value.width : 1;
  const style = value.style ?? "solid";
  return `${width}px ${style} ${value.color}`;
}
function border(value) {
  if (!value) return {};
  if (typeof value === "string") {
    return value === "" ? {} : { border: value };
  }
  if (isBorderStyle(value)) {
    const css = borderStyleToCss(value);
    return css ? { border: css } : {};
  }
  const out = {};
  const top = borderStyleToCss(value.top);
  const right = borderStyleToCss(value.right);
  const bottom = borderStyleToCss(value.bottom);
  const left = borderStyleToCss(value.left);
  if (top) out["border-top"] = top;
  if (right) out["border-right"] = right;
  if (bottom) out["border-bottom"] = bottom;
  if (left) out["border-left"] = left;
  return out;
}
function borderSide(value) {
  if (value === void 0 || value === null || value === "") return void 0;
  if (typeof value === "string") return value;
  return borderStyleToCss(value);
}
function isBorderStyle(value) {
  return "color" in value || "width" in value || "style" in value && typeof value.style === "string";
}
function asString(value) {
  if (value === void 0 || value === null || typeof value === "object") return void 0;
  return String(value);
}
function joinClasses(...tokens) {
  const parts = tokens.filter((t) => typeof t === "string" && t.length > 0);
  return parts.length ? parts.join(" ") : void 0;
}
function escapeHtml(value) {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
var SECTION_ROLE_CLASSES = {
  header: "nm-sec-header",
  footer: "nm-sec-footer"
};
function sectionRoleOf(node) {
  const declared = node.meta?.aiHints?.role?.toLowerCase();
  if (declared === "footer" || declared === "header") return declared;
  const name = node.name?.toLowerCase() ?? "";
  if (/^footer$/.test(name.trim())) return "footer";
  if (/^header$/.test(name.trim())) return "header";
  return void 0;
}
function sectionRoleClass(node) {
  const role = sectionRoleOf(node);
  return role ? SECTION_ROLE_CLASSES[role] : void 0;
}

// src/email-builder/compiler/compile-chat-template.ts
var EMAIL_WIDTH = 600;
function compileChatTemplate(document) {
  const ctx = { warnings: [] };
  const bodyChildren = document.root.children.slice(1);
  const sectionsHtml = bodyChildren.map((child) => emitTopLevel(child, ctx)).filter((s) => s.length > 0).join("\n\n");
  return { html: sectionsHtml + "\n", warnings: ctx.warnings };
}
function emitTopLevel(node, ctx) {
  switch (node.type) {
    case "section":
      return emitSection(node, ctx);
    case "wrapper":
      return emitWrapper(node, ctx);
    case "canvas-section":
      return emitCanvasSection(node, ctx);
    default:
      ctx.warnings.push(
        `compile-chat-template: unexpected top-level node type '${node.type}', dropped`
      );
      return "";
  }
}
function emitSection(node, ctx) {
  const sectionName = sectionNameFor(node);
  const styleStr = sectionStyle(node);
  const inner = node.children.map((child) => emitColumnContainer(child, ctx)).filter((s) => s.length > 0).join("\n  ");
  return `<section data-section-name="${escapeAttr(sectionName)}" style="${styleStr}">
  ${inner}
</section>`;
}
function emitCanvasSection(node, ctx) {
  const sectionName = canvasSectionNameFor(node);
  const sectionStyle2 = canvasSectionStyle(node);
  const cached = node.cached;
  if (cached?.html) {
    return `<section data-section-name="${escapeAttr(sectionName)}" style="${sectionStyle2}">
  ${cached.html}
</section>`;
  }
  if (cached?.url) {
    const width = cached.width ?? node.props.scene?.width ?? EMAIL_WIDTH;
    const height = cached.height ?? node.props.scene?.height ?? Math.round(width * 0.625);
    const alt = node.props.alt ?? "";
    const img = `<img src="${escapeAttr(cached.url)}" alt="${escapeAttr(alt)}" width="${width}" height="${height}" style="display:block;width:100%;max-width:${width}px;height:auto;">`;
    const href = node.props.href;
    const inner = href ? `<a href="${escapeAttr(href)}" data-link-zone data-utm-content="${escapeAttr(sectionName)}" style="text-decoration:none;color:inherit;display:block;">${img}</a>` : img;
    return `<section data-section-name="${escapeAttr(sectionName)}" style="${sectionStyle2}">
  ${inner}
</section>`;
  }
  ctx.warnings.push(
    `compile-chat-template: canvas-section "${sectionName}" has no cached.url or cached.html; emitting a placeholder. The caller must rasterize canvas-sections before compile.`
  );
  return `<section data-section-name="${escapeAttr(sectionName)}" style="${sectionStyle2}">
  <div style="min-height:200px;background:#f3f4f6;text-align:center;padding:40px;color:#6b7280;font-family:sans-serif;">Canvas section (not yet rendered)</div>
</section>`;
}
function canvasSectionNameFor(node) {
  if (node.name && node.name.length > 0) return slugify(node.name);
  if (node.props.alt) return slugify(node.props.alt);
  if (node.id) return slugify(node.id);
  return "canvas-section";
}
function canvasSectionStyle(node) {
  const parts = [
    `width:${EMAIL_WIDTH}px`,
    "box-sizing:border-box",
    "margin:0 auto"
  ];
  const bg = node.props.scene?.backgroundColor;
  if (bg) parts.push(`background-color:${bg}`);
  const padding = spacingToCss(node.style?.padding);
  if (padding) parts.push(`padding:${padding}`);
  return parts.join(";") + ";";
}
function emitWrapper(node, ctx) {
  if (node.style?.backgroundColor || node.style?.backgroundImage || node.style?.padding || node.style?.borderRadius) {
    ctx.warnings.push(
      `compile-chat-template: wrapper style (bg/padding/radius) is lost on the way to chat-mode; fold it onto a child section.`
    );
  }
  return node.children.map((child) => emitTopLevel(child, ctx)).filter((s) => s.length > 0).join("\n\n");
}
function sectionStyle(node) {
  const parts = [
    `width:${EMAIL_WIDTH}px`,
    "box-sizing:border-box",
    "margin:0 auto"
  ];
  const bg = node.style?.backgroundColor;
  if (bg) parts.push(`background-color:${bg}`);
  const padding = spacingToCss(node.style?.padding);
  if (padding) parts.push(`padding:${padding}`);
  const radius = cornerRadiusToCss(node.style?.borderRadius);
  if (radius) parts.push(`border-radius:${radius}`);
  return parts.join(";") + ";";
}
function sectionNameFor(node) {
  if (node.name && node.name.length > 0) return slugify(node.name);
  if (node.id) return slugify(node.id);
  return "section";
}
function emitColumnContainer(node, ctx) {
  if (node.type === "product-feed") {
    return emitProductFeed(node, ctx);
  }
  if (node.type === "group") {
    return emitGroup(node, ctx);
  }
  return emitColumnContent(node, ctx);
}
function emitGroup(node, ctx) {
  const innerWidth = 552;
  const cellWidth = Math.floor(innerWidth / Math.max(node.children.length, 1));
  const tds = node.children.map((col) => {
    const colInner = emitColumnContent(col, ctx);
    const tdStyle = columnTdStyle(col, cellWidth);
    return `      <td style="${tdStyle}">
        ${colInner}
      </td>`;
  }).join("\n");
  return `<table role="presentation" width="${innerWidth}" cellpadding="0" cellspacing="0" style="width:${innerWidth}px;margin:0 auto;">
    <tr>
${tds}
    </tr>
  </table>`;
}
function columnTdStyle(col, defaultWidth) {
  const parts = [];
  const width = typeof col.props.width === "string" && col.props.width.endsWith("px") ? col.props.width : `${defaultWidth}px`;
  parts.push(`width:${width}`);
  parts.push(`vertical-align:${col.props.verticalAlign ?? "top"}`);
  if (col.style?.backgroundColor) {
    parts.push(`background-color:${col.style.backgroundColor}`);
  }
  const padding = spacingToCss(col.style?.padding);
  if (padding) parts.push(`padding:${padding}`);
  const radius = cornerRadiusToCss(col.style?.borderRadius);
  if (radius) parts.push(`border-radius:${radius}`);
  return parts.join(";") + ";";
}
function emitColumnContent(node, ctx) {
  return node.children.map((child) => emitContentNode(child, ctx)).filter((s) => s.length > 0).join("\n  ");
}
function emitContentNode(node, ctx) {
  switch (node.type) {
    case "text":
      return emitText(node);
    case "image":
      return emitImage(node);
    case "button":
      return emitButton(node);
    case "divider":
      return emitDivider(node);
    case "spacer":
      return emitSpacer(node);
    case "social":
      return emitSocial(node, ctx);
    case "navbar":
      return emitNavbar(node, ctx);
    default:
      ctx.warnings.push(
        `compile-chat-template: unsupported content node type '${node.type}', dropped`
      );
      return "";
  }
}
function emitText(node) {
  const tag = textTagForRole(node.props.role);
  const styleStr = textStyle(node);
  const inner = node.props.format === "html" ? node.props.content : escapeHtml(node.props.content);
  return `<${tag} style="${styleStr}">${inner}</${tag}>`;
}
function textTagForRole(role) {
  switch (role) {
    case "headline":
      return "h1";
    case "subheadline":
      return "h2";
    case "caption":
    case "eyebrow":
    case "legal":
      return "p";
    case "body":
    default:
      return "p";
  }
}
function textStyle(node) {
  const parts = [];
  if (node.style?.fontFamily) parts.push(`font-family:${node.style.fontFamily}`);
  if (node.style?.fontSize !== void 0) {
    parts.push(`font-size:${node.style.fontSize}px`);
  }
  if (node.style?.fontWeight !== void 0) {
    parts.push(`font-weight:${node.style.fontWeight}`);
  }
  if (node.style?.lineHeight !== void 0) {
    parts.push(`line-height:${node.style.lineHeight}`);
  }
  if (node.style?.letterSpacing !== void 0) {
    parts.push(`letter-spacing:${node.style.letterSpacing}px`);
  }
  if (node.style?.color) parts.push(`color:${node.style.color}`);
  if (node.style?.align) parts.push(`text-align:${node.style.align}`);
  const padding = spacingToCss(node.style?.padding);
  parts.push(`margin:0`);
  if (padding) parts.push(`padding:${padding}`);
  return parts.join(";") + ";";
}
function emitImage(node) {
  const src = imageSrc(node);
  const alt = node.props.alt ?? "";
  const widthAttr = imageWidthAttr(node);
  const heightAttr = imageHeightAttr(node);
  const styleStr = imageStyle(node);
  const imgTag = `<img src="${escapeAttr(src)}" alt="${escapeAttr(alt)}"${widthAttr}${heightAttr} style="${styleStr}">`;
  if (node.props.href) {
    return `<a href="${escapeAttr(node.props.href)}" style="text-decoration:none;color:inherit;">${imgTag}</a>`;
  }
  return imgTag;
}
function imageSrc(node) {
  const src = node.props.src;
  if (!src) return "";
  if (typeof src === "string") return src;
  if ("$brandAsset" in src) {
    return src.$brandAsset === "logo" ? "$brand.logoUrl" : `$brand.asset.${src.$brandAsset}`;
  }
  return "";
}
function imageWidthAttr(node) {
  const w = node.style?.width;
  if (w === void 0) return "";
  if (w === "100%") return ` width="${EMAIL_WIDTH}"`;
  return ` width="${w}"`;
}
function imageHeightAttr(node) {
  const h = node.style?.height;
  if (h === void 0) return "";
  return ` height="${h}"`;
}
function imageStyle(node) {
  const parts = ["display:block"];
  if (node.style?.width !== void 0) {
    const w = node.style.width === "100%" ? "100%" : `${node.style.width}px`;
    parts.push(`width:${w}`);
  }
  if (node.style?.height !== void 0) {
    parts.push(`height:${node.style.height}px`);
  }
  if (node.style?.fit === "cover" || node.style?.fit === "smart-crop") {
    parts.push("object-fit:cover");
  }
  const radius = cornerRadiusToCss(node.style?.borderRadius);
  if (radius) parts.push(`border-radius:${radius}`);
  const padding = spacingToCss(node.style?.padding);
  if (padding) parts.push(`padding:${padding}`);
  if (node.style?.align === "center") parts.push("margin:0 auto");
  return parts.join(";") + ";";
}
function emitButton(node) {
  const utm = slugify(node.props.label || "button");
  const buttonStyle = buttonInlineStyle(node);
  const wrapperStyle = "text-decoration:none;color:inherit;display:inline-block;";
  return `<a href="${escapeAttr(node.props.href)}" data-link-zone data-utm-content="${escapeAttr(utm)}" style="${wrapperStyle}"><span data-component="button" style="${buttonStyle}">${escapeHtml(node.props.label)}</span></a>`;
}
function buttonInlineStyle(node) {
  const parts = ["display:inline-block"];
  if (node.style?.backgroundColor) {
    parts.push(`background-color:${node.style.backgroundColor}`);
  }
  if (node.style?.color) parts.push(`color:${node.style.color}`);
  if (node.style?.fontSize !== void 0) {
    parts.push(`font-size:${node.style.fontSize}px`);
  }
  if (node.style?.fontWeight !== void 0) {
    parts.push(`font-weight:${node.style.fontWeight}`);
  }
  const padding = spacingToCss(node.style?.padding) ?? "14px 32px";
  parts.push(`padding:${padding}`);
  const radius = cornerRadiusToCss(node.style?.borderRadius);
  if (radius) parts.push(`border-radius:${radius}`);
  return parts.join(";") + ";";
}
function emitDivider(node) {
  const parts = ["border:none"];
  const color = node.style?.color ?? "#e5e7eb";
  const thickness = node.style?.thickness ?? 1;
  parts.push(`border-top:${thickness}px solid ${color}`);
  if (node.style?.width) parts.push(`width:${node.style.width}`);
  parts.push("margin:0");
  const padding = spacingToCss(node.style?.padding);
  if (padding) parts.push(`padding:${padding}`);
  return `<hr style="${parts.join(";")};">`;
}
function emitSpacer(node) {
  return `<div style="height:${node.props.height}px;line-height:${node.props.height}px;font-size:1px;">&nbsp;</div>`;
}
function emitSocial(node, ctx) {
  const size = node.style?.iconSize ?? 24;
  const align = node.style?.align ?? "center";
  const wantsLightIcons = isLightColor(node.style?.iconColor);
  const filter = wantsLightIcons ? "filter:invert(1);" : "";
  const links = node.props.links.map((l) => {
    const src = l.iconUrl || socialIconUrl(l.platform);
    return `<a href="${escapeAttr(l.href)}" data-link-zone data-utm-content="social-${escapeAttr(l.platform)}" style="text-decoration:none;display:inline-block;margin:0 8px;vertical-align:middle;"><img src="${escapeAttr(src)}" alt="${escapeAttr(l.platform)}" width="${size}" height="${size}" style="display:inline-block;width:${size}px;height:${size}px;border:0;${filter}"></a>`;
  }).join("");
  if (node.props.links.length === 0) {
    ctx.warnings.push("compile-chat-template: social node has no links, emitted empty row");
  }
  return `<div style="text-align:${align};">${links}</div>`;
}
function isLightColor(color) {
  if (!color) return false;
  const hex = color.trim().replace(/^#/, "");
  const full = hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) return false;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6;
}
function emitNavbar(node, ctx) {
  ctx.warnings.push(
    `compile-chat-template: navbar node emitted as plain links (Phase 1 stub)`
  );
  const links = node.props.links.map(
    (l, i) => `<a href="${escapeAttr(l.href)}" data-link-zone data-utm-content="nav-${i}" style="text-decoration:none;color:inherit;margin:0 12px;">${escapeHtml(l.label)}</a>`
  ).join("");
  const align = node.style?.align ?? "center";
  return `<div style="text-align:${align};">${links}</div>`;
}
function emitProductFeed(node, ctx) {
  ctx.warnings.push(
    `compile-chat-template: product-feed emitted as data-only placeholder (Phase 1 stub)`
  );
  const cfg = JSON.stringify(node.props);
  return `<div data-product-feed="${escapeAttr(cfg)}" style="min-height:120px;background:#f3f4f6;">Product feed (${node.props.layout})</div>`;
}
function spacingToCss(value) {
  if (value === void 0) return void 0;
  if (typeof value === "number") return `${value}px`;
  return `${value.top ?? 0}px ${value.right ?? 0}px ${value.bottom ?? 0}px ${value.left ?? 0}px`;
}
function cornerRadiusToCss(value) {
  if (value === void 0) return void 0;
  if (typeof value === "number") return `${value}px`;
  if (typeof value === "string") return value;
  const v = value;
  const corner = (x) => x === void 0 ? "0" : `${x}px`;
  return `${corner(v.tl)} ${corner(v.tr)} ${corner(v.br)} ${corner(v.bl)}`;
}
function escapeAttr(value) {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function slugify(value) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 64) || "item";
}

// src/email-builder/section-row-to-chat-html.ts
function slug(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "section";
}
function ensureSectionWrapper(html, name) {
  if (/^\s*<section\b/i.test(html)) return html;
  return `<section data-section-name="${slug(name)}" style="width:600px;margin:0 auto;box-sizing:border-box;">${html}</section>`;
}
function isChatInsertable(row) {
  if (row.sourceFormat === "node") {
    return !!row.node && row.node.type !== "canvas-section";
  }
  if (row.sourceFormat === "html") return !!row.rawHtml?.trim();
  return false;
}
function sectionRowToChatHtml(row, brand) {
  if (row.sourceFormat === "node" && row.node) {
    const node = row.node;
    if (node.type === "canvas-section") return null;
    const resolved = resolveSectionForInsert({
      node,
      slots: row.slots ?? {},
      slotDefaults: row.slotDefaults ?? {},
      brand,
      mediaOffset: mediaOffsetForId(row.id)
    });
    const doc = createEmptyEmailDocument("chat-add-section");
    doc.root.children.push(resolved);
    const { html } = compileChatTemplate(doc);
    const out = html.trim();
    return out || null;
  }
  if (row.sourceFormat === "html" && typeof row.rawHtml === "string" && row.rawHtml.trim()) {
    return ensureSectionWrapper(row.rawHtml.trim(), row.name);
  }
  return null;
}

// src/email-builder/product-binding.ts
function findProductBlockRoot(doc, nodeId) {
  let current = doc.nodes[nodeId];
  while (current) {
    if (current.productBinding) return current.id;
    current = current.parentId ? doc.nodes[current.parentId] : void 0;
  }
  return null;
}
function collectProductFieldNodes(doc, rootId) {
  const out = [];
  const walk3 = (id, isRoot) => {
    const node = doc.nodes[id];
    if (!node) return;
    if (!isRoot && node.productBinding) return;
    if (node.productField) out.push({ node, field: node.productField });
    for (const childId of node.children ?? []) walk3(childId, false);
  };
  walk3(rootId, true);
  return out;
}
function formatProductPrice(value, currencyCode = "USD") {
  if (value == null || value === "") return void 0;
  if (typeof value === "string" && !/^\s*-?\d+(\.\d+)?\s*$/.test(value)) {
    return value;
  }
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return void 0;
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currencyCode }).format(num);
  } catch {
    return `$${num.toFixed(2)}`;
  }
}
function parsePriceAmount(value) {
  if (!value) return null;
  const cleaned = value.replace(/[^\d.,-]/g, "").replace(/,/g, "");
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
}
function buildProductApplyPatches(doc, rootId, product, opts = {}) {
  const root = doc.nodes[rootId];
  if (!root) return [];
  const preferTransparent = opts.preferTransparent !== false;
  const imageUrl = (preferTransparent ? product.transparentImageUrl : void 0) ?? product.fallbackImageUrl;
  const productUrl = product.productUrl || void 0;
  const price = formatProductPrice(product.price, opts.currencyCode);
  const compareAt = formatProductPrice(product.compareAtPrice, opts.currencyCode);
  const priceAmount = parsePriceAmount(price);
  const compareAmount = parsePriceAmount(compareAt);
  const compareIsMarkdown = compareAt != null && (priceAmount == null || compareAmount == null || compareAmount > priceAmount);
  const patches = [];
  for (const { node, field } of collectProductFieldNodes(doc, rootId)) {
    switch (field) {
      case "image":
        patches.push({
          nodeId: node.id,
          props: {
            imageSrc: imageUrl,
            imageAlt: product.name,
            // Never inherit the previous product's crop — authored transforms
            // are relative to cover-fit of the OLD bitmap. Clearing falls
            // back to a clean cover fit of the new image.
            imageTransform: void 0,
            ...productUrl ? { linkUrl: productUrl } : {}
          }
        });
        break;
      case "title":
        patches.push({ nodeId: node.id, props: { text: product.name } });
        break;
      case "price":
        if (price != null) patches.push({ nodeId: node.id, props: { text: price } });
        break;
      case "compareAtPrice":
        patches.push({
          nodeId: node.id,
          props: compareIsMarkdown ? { text: compareAt, visible: true } : { visible: false }
        });
        break;
      case "description":
        if (opts.description != null) {
          patches.push({ nodeId: node.id, props: { text: opts.description } });
        }
        break;
      case "cta":
        if (productUrl) patches.push({ nodeId: node.id, props: { linkUrl: productUrl } });
        break;
      case "vendor":
        break;
    }
  }
  const snapshot = {
    name: product.name,
    price,
    compareAtPrice: compareIsMarkdown ? compareAt : void 0,
    productUrl: product.productUrl,
    imageUrl
  };
  patches.push({
    nodeId: rootId,
    props: {
      productBinding: { productId: product.productId, variantId: product.variantId },
      // Whole-card click-through in link-split / image compile modes.
      ...productUrl ? { linkUrl: productUrl } : {},
      pluginData: { ...root.pluginData ?? {}, productSnapshot: snapshot }
    }
  });
  return patches;
}
function readProductSnapshot(node) {
  const raw = node?.pluginData?.productSnapshot;
  if (!raw || typeof raw !== "object") return null;
  const snap = raw;
  if (typeof snap.name !== "string") return null;
  return {
    name: snap.name,
    price: typeof snap.price === "string" ? snap.price : void 0,
    compareAtPrice: typeof snap.compareAtPrice === "string" ? snap.compareAtPrice : void 0,
    productUrl: typeof snap.productUrl === "string" ? snap.productUrl : "",
    imageUrl: typeof snap.imageUrl === "string" ? snap.imageUrl : ""
  };
}

// src/email-builder/default-section-resolver.ts
var HEADER_REF_NAME = "$brand.defaultHeader";
var FOOTER_REF_NAME = "$brand.defaultFooter";
function isDefaultSectionRef(node) {
  if (!node || typeof node !== "object") return false;
  const obj = node;
  if (obj.type !== "section-ref") return false;
  return obj.section === HEADER_REF_NAME || obj.section === FOOTER_REF_NAME;
}
function resolveDefaultSectionRefs(document, args) {
  const stats = { resolved: 0, dropped: 0 };
  walk(document, args, stats);
  return stats;
}
function walk(node, args, stats) {
  if (!node || typeof node !== "object") return;
  if (Array.isArray(node)) {
    for (let i = node.length - 1; i >= 0; i--) {
      const item = node[i];
      if (isDefaultSectionRef(item)) {
        const replacement = resolvePlaceholder(item, args);
        if (replacement === null) {
          node.splice(i, 1);
          stats.dropped++;
        } else {
          node[i] = replacement;
          stats.resolved++;
        }
      } else if (item && typeof item === "object") {
        walk(item, args, stats);
      }
    }
    return;
  }
  const obj = node;
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (isDefaultSectionRef(v)) {
      const replacement = resolvePlaceholder(v, args);
      if (replacement === null) {
        delete obj[k];
        stats.dropped++;
      } else {
        obj[k] = replacement;
        stats.resolved++;
      }
    } else if (v && typeof v === "object") {
      walk(v, args, stats);
    }
  }
}
function resolvePlaceholder(ref, args) {
  const source = ref.section === HEADER_REF_NAME ? args.defaultHeader : args.defaultFooter;
  if (!source) return null;
  return resolveSectionForInsert({
    node: source.node,
    slots: source.slots,
    slotDefaults: source.slotDefaults,
    brand: args.brand,
    usedMediaUrls: args.usedMediaUrls,
    usedProductIds: args.usedProductIds,
    pickRecentMedia: args.pickRecentMedia,
    pickProduct: args.pickProduct,
    pickProductRich: args.pickProductRich,
    pickTransparentProductMedia: args.pickTransparentProductMedia,
    mediaOffset: args.mediaOffset
  });
}

// src/email-builder/template-slot-extractor.ts
var SLOT_REGEX = /\$slot\.([a-zA-Z][a-zA-Z0-9_-]*)(?:\.([a-zA-Z][a-zA-Z0-9_-]*))?/g;
function extractSlots(doc) {
  const seen = /* @__PURE__ */ new Map();
  walk2(doc.root, seen);
  return [...seen.values()];
}
function walk2(node, seen) {
  if (node == null || typeof node !== "object") return;
  const n = node;
  const type = n.type;
  const props = n.props ?? {};
  const style = n.style ?? {};
  switch (type) {
    case "text": {
      record(seen, asString2(props.content), "text", String(props.role ?? "body"), "copy");
      break;
    }
    case "image": {
      record(seen, asString2(props.src), "image-src", asString2(props.alt) ?? "", "image-url");
      record(seen, asString2(props.alt), "image-alt", "", "copy");
      record(seen, asString2(props.href), "button-href", "", "href");
      break;
    }
    case "button": {
      record(seen, asString2(props.label), "button-label", "", "copy");
      record(seen, asString2(props.href), "button-href", "", "href");
      break;
    }
    case "section": {
      record(seen, asString2(style.backgroundImage), "section-bg-image", "", "image-url");
      record(seen, asString2(props.href), "button-href", "", "href");
      break;
    }
    case "product-feed": {
      const source = props.source;
      if (source?.kind === "manual" && Array.isArray(source.productIds)) {
        for (const id of source.productIds) {
          record(seen, asString2(id), "product-feed-id", "", "product-id");
        }
      }
      break;
    }
    case "canvas-section": {
      const canvas = props.canvasJson;
      const nodes = canvas?.nodes;
      if (nodes && typeof nodes === "object") {
        for (const sceneNode of Object.values(nodes)) {
          const sn = sceneNode;
          const sType = asString2(sn.type);
          if (sType === "IMAGE") {
            record(seen, asString2(sn.imageSrc), "image-src", asString2(sn.imageAlt) ?? "", "image-url");
            record(seen, asString2(sn.imageAlt), "image-alt", "", "copy");
          } else if (sType === "TEXT") {
            record(seen, asString2(sn.text), "text", asString2(sn.slotRole) ?? "body", "copy");
          } else if (sType === "LINK_ZONE") {
            record(seen, asString2(sn.linkUrl), "button-href", "", "href");
            record(seen, asString2(sn.imageAlt), "image-alt", "", "copy");
          }
        }
      }
      break;
    }
  }
  const children = n.children ?? [];
  if (Array.isArray(children)) {
    for (const c of children) walk2(c, seen);
  }
}
function asString2(v) {
  return typeof v === "string" ? v : void 0;
}
function record(seen, value, site, context, expects) {
  if (!value) return;
  const matches = value.matchAll(SLOT_REGEX);
  for (const m of matches) {
    const name = m[1];
    const subPath = m[2];
    if (seen.has(name)) continue;
    const effectiveExpects = subPath ? "product-id" : expects;
    seen.set(name, { name, site, context: context || void 0, expects: effectiveExpects });
  }
}
function buildPreviewSlotDefaults(slots, pools) {
  const out = {};
  let mediaIdx = 0;
  let productIdx = 0;
  for (const slot of slots) {
    switch (slot.expects) {
      case "image-url": {
        const url = pools.mediaUrls[mediaIdx % Math.max(1, pools.mediaUrls.length)];
        if (url) {
          out[slot.name] = url;
          mediaIdx++;
        }
        break;
      }
      case "product-id": {
        const id = pools.productVariantIds[productIdx % Math.max(1, pools.productVariantIds.length)];
        if (id) {
          out[slot.name] = id;
          productIdx++;
        }
        break;
      }
      case "href": {
        out[slot.name] = pools.brandWebsite ?? "#";
        break;
      }
      case "copy": {
        out[slot.name] = humanizeSlotName(slot.name);
        break;
      }
      default:
        break;
    }
  }
  return out;
}
function humanizeSlotName(name) {
  return name.replace(/[-_]/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/\b\w/g, (c) => c.toUpperCase());
}
function extractSlotGroups(doc) {
  const imageGroups = [];
  const productGroups = [];
  walkForGroups(doc.root, void 0, imageGroups, productGroups);
  return {
    imageGroups: imageGroups.filter((g) => g.slotNames.length > 0),
    productGroups: productGroups.filter((g) => g.slotNames.length > 0)
  };
}
function walkForGroups(node, parentId, imageGroups, productGroups) {
  if (node == null || typeof node !== "object") return;
  const n = node;
  const id = typeof n.id === "string" ? n.id : "";
  const type = n.type;
  if (type === "canvas-section") {
    const props = n.props ?? {};
    const canvas = props.canvasJson;
    const nodes = canvas?.nodes;
    if (nodes && typeof nodes === "object") {
      const slotNames = [];
      for (const sn of Object.values(nodes)) {
        const sceneNode = sn;
        if (sceneNode.type === "IMAGE" && typeof sceneNode.imageSrc === "string") {
          const m = /^\$slot\.([a-zA-Z][a-zA-Z0-9_-]*)$/.exec(sceneNode.imageSrc);
          if (m) slotNames.push(m[1]);
        }
      }
      if (slotNames.length > 0) {
        imageGroups.push({
          kind: "image",
          groupId: id || `canvas-${imageGroups.length}`,
          slotNames,
          columns: Math.min(3, slotNames.length)
        });
      }
    }
    return;
  }
  if (type === "product-feed") {
    const props = n.props ?? {};
    const source = props.source;
    if (source?.kind === "manual" && Array.isArray(source.productIds)) {
      const slotNames = [];
      for (const pid of source.productIds) {
        if (typeof pid === "string") {
          const m = /^\$slot\.([a-zA-Z][a-zA-Z0-9_-]*)$/.exec(pid);
          if (m) slotNames.push(m[1]);
        }
      }
      if (slotNames.length > 0) {
        productGroups.push({
          kind: "product",
          groupId: id || `feed-${productGroups.length}`,
          slotNames,
          columns: layoutToColumns(typeof props.layout === "string" ? props.layout : void 0)
        });
      }
    }
    return;
  }
  if (type === "column" || type === "group" || type === "section" || type === "wrapper") {
    const children2 = Array.isArray(n.children) ? n.children : [];
    const imageSlots = [];
    for (const child of children2) {
      if (child && typeof child === "object") {
        const c = child;
        if (c.type === "image") {
          const cp = c.props ?? {};
          const src = cp.src;
          if (typeof src === "string") {
            const m = /^\$slot\.([a-zA-Z][a-zA-Z0-9_-]*)$/.exec(src);
            if (m) imageSlots.push(m[1]);
          }
        }
      }
    }
    if (imageSlots.length > 0) {
      imageGroups.push({
        kind: "image",
        groupId: id || `imgs-${imageGroups.length}`,
        slotNames: imageSlots,
        // Image groups: column count = number of siblings (so 3 images side
        // by side render as 3-col tiles). Cap at 3 to keep tiles readable.
        columns: Math.min(3, imageSlots.length)
      });
    }
    for (const child of children2) walkForGroups(child, id, imageGroups, productGroups);
    return;
  }
  const children = n.children ?? [];
  if (Array.isArray(children)) {
    for (const c of children) walkForGroups(c, parentId, imageGroups, productGroups);
  }
}
function layoutToColumns(layout) {
  switch (layout) {
    case "grid-3":
      return 3;
    case "grid-2":
      return 2;
    case "feature-first":
      return 2;
    case "stack":
      return 1;
    default:
      return 3;
  }
}

// src/email-builder/chat-style-overrides.ts
var OVERRIDE_BLOCK_ID = "nm-chat-overrides";
var escapeCssValue = (value) => value.replace(/[<>"]/g, "").trim();
var buildBlock = (opts) => {
  const bg = typeof opts.bg === "string" && opts.bg.trim().length > 0 ? escapeCssValue(opts.bg) : null;
  const font = typeof opts.fontFamily === "string" && opts.fontFamily.trim().length > 0 ? escapeCssValue(opts.fontFamily) : null;
  if (!bg && !font) return null;
  const bodyDecls = [];
  if (bg) bodyDecls.push(`background-color:${bg} !important`);
  if (font) bodyDecls.push(`font-family:${font} !important`);
  const tableDecls = bg ? `background-color:${bg} !important;` : "";
  const rules = [];
  rules.push(`body{${bodyDecls.join(";")}}`);
  if (tableDecls) {
    rules.push(`table.body,td.body,.email-body{${tableDecls}}`);
  }
  return `<style id="${OVERRIDE_BLOCK_ID}">${rules.join("")}</style>`;
};
var STRIP_RE = new RegExp(
  `<style[^>]*\\bid=["']${OVERRIDE_BLOCK_ID}["'][^>]*>[\\s\\S]*?<\\/style>`,
  "i"
);
function applyChatStyleOverrides(html, opts) {
  if (typeof html !== "string" || html.length === 0) return html;
  const stripped = html.replace(STRIP_RE, "");
  const block = buildBlock(opts);
  if (!block) return stripped;
  const headOpenMatch = stripped.match(/<head[^>]*>/i);
  if (headOpenMatch && typeof headOpenMatch.index === "number") {
    const insertAt = headOpenMatch.index + headOpenMatch[0].length;
    return stripped.slice(0, insertAt) + block + stripped.slice(insertAt);
  }
  const htmlOpenMatch = stripped.match(/<html[^>]*>/i);
  if (htmlOpenMatch && typeof htmlOpenMatch.index === "number") {
    const insertAt = htmlOpenMatch.index + htmlOpenMatch[0].length;
    return stripped.slice(0, insertAt) + `<head>${block}</head>` + stripped.slice(insertAt);
  }
  return block + stripped;
}

// src/email-builder/chat-template-font-merge.ts
var HEADING_DISPLAY_SANS = /* @__PURE__ */ new Set([
  "anton",
  "bebas neue",
  "oswald",
  "impact",
  "archivo black",
  "permanent marker",
  "bangers",
  "fjalla one",
  "staatliches",
  "alfa slab one",
  "bowlby one",
  "rubik mono one",
  "abril fatface",
  "monoton",
  "unica one"
]);
function stripQuotes(name) {
  return name.replace(/^['"]|['"]$/g, "").trim();
}
function classifyStackAsHeading(stack) {
  const names = stack.split(",").map((s) => stripQuotes(s.trim()).toLowerCase());
  if (names.length === 0) return false;
  const generic = names[names.length - 1];
  if (generic === "serif" || generic === "cursive" || generic === "fantasy") {
    return true;
  }
  const first = names[0];
  if (HEADING_DISPLAY_SANS.has(first)) return true;
  return false;
}
function stackAlreadyLedBy(stack, brandFont) {
  const first = stripQuotes(stack.split(",")[0]?.trim() ?? "").toLowerCase();
  return first.length > 0 && first === stripQuotes(brandFont).toLowerCase();
}
function quoteIfNeeded(name) {
  const trimmed = name.trim();
  if (!trimmed) return trimmed;
  if (/^['"].*['"]$/.test(trimmed)) return trimmed;
  if (/\s/.test(trimmed)) return `'${trimmed.replace(/'/g, "")}'`;
  return trimmed;
}
function rewriteFontFamilyInStyle(style, brand) {
  return style.replace(
    /font-family\s*:\s*([^;]+)/gi,
    (match, rawValue) => {
      const value = rawValue.trim();
      if (value.length === 0) return match;
      if (value.includes("$brand.font.")) return match;
      const isHeading = classifyStackAsHeading(value);
      const brandFont = isHeading ? brand.heading : brand.body;
      if (!brandFont || brandFont.trim().length === 0) return match;
      if (stackAlreadyLedBy(value, brandFont)) return match;
      return `font-family:${quoteIfNeeded(brandFont)}, ${value}`;
    }
  );
}
function mergeBrandFontsIntoChatHtml(html, brand) {
  if (typeof html !== "string" || html.length === 0) return html;
  const hasBrandFonts = typeof brand.heading === "string" && brand.heading.trim().length > 0 || typeof brand.body === "string" && brand.body.trim().length > 0;
  if (!hasBrandFonts) return html;
  return html.replace(/style="([^"]*)"/gi, (match, styleBody) => {
    if (!/font-family/i.test(styleBody)) return match;
    const rewritten = rewriteFontFamilyInStyle(styleBody, brand);
    if (rewritten === styleBody) return match;
    return `style="${rewritten}"`;
  });
}

// src/email-builder/decoration-orientation.ts
function classifyDecorationEdge(pathData, viewBoxHeight) {
  if (viewBoxHeight <= 0) return "symmetric";
  const subpaths = flattenPath(pathData);
  let cySum = 0;
  let aSum = 0;
  for (const poly of subpaths) {
    if (poly.length < 3) continue;
    let area2 = 0;
    let cyAcc = 0;
    for (let i = 0; i < poly.length - 1; i++) {
      const [x1, y1] = poly[i];
      const [x2, y2] = poly[i + 1];
      const cross = x1 * y2 - x2 * y1;
      area2 += cross;
      cyAcc += (y1 + y2) * cross;
    }
    const a = area2 / 2;
    if (a === 0) continue;
    const polyCy = cyAcc / (6 * a);
    cySum += polyCy * Math.abs(a);
    aSum += Math.abs(a);
  }
  if (aSum === 0) return "symmetric";
  const norm = cySum / aSum / viewBoxHeight;
  if (norm < 0.48) return "bottom";
  if (norm > 0.52) return "top";
  return "symmetric";
}
function flattenPath(d) {
  const tokens = d.match(/[A-Za-z]|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g) ?? [];
  const subpaths = [];
  let current = [];
  let start = [0, 0];
  let cursor = [0, 0];
  let cmd = "";
  let nums = [];
  const flush = () => {
    if (current.length > 0) subpaths.push(current);
    current = [];
  };
  const ensure = () => {
    if (current.length === 0) current.push([cursor[0], cursor[1]]);
  };
  const flatQuad = (p0, p1, p2, steps = 24) => {
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const u = 1 - t;
      const x = u * u * p0[0] + 2 * u * t * p1[0] + t * t * p2[0];
      const y = u * u * p0[1] + 2 * u * t * p1[1] + t * t * p2[1];
      current.push([x, y]);
    }
  };
  const flatCubic = (p0, p1, p2, p3, steps = 32) => {
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const u = 1 - t;
      const x = u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0];
      const y = u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1];
      current.push([x, y]);
    }
  };
  const consume = () => {
    const c = cmd;
    const isRel = c === c.toLowerCase();
    if (c.toUpperCase() === "Z") {
      if (current.length > 0) current.push([start[0], start[1]]);
      cursor = [start[0], start[1]];
      flush();
      nums = [];
      return;
    }
    while (nums.length > 0) {
      switch (c.toUpperCase()) {
        case "M": {
          if (nums.length < 2) return;
          const x = isRel ? cursor[0] + nums.shift() : nums.shift();
          const y = isRel ? cursor[1] + nums.shift() : nums.shift();
          flush();
          cursor = [x, y];
          start = [x, y];
          current.push([x, y]);
          cmd = isRel ? "l" : "L";
          break;
        }
        case "L": {
          if (nums.length < 2) return;
          const x = isRel ? cursor[0] + nums.shift() : nums.shift();
          const y = isRel ? cursor[1] + nums.shift() : nums.shift();
          ensure();
          current.push([x, y]);
          cursor = [x, y];
          break;
        }
        case "H": {
          if (nums.length < 1) return;
          const x = isRel ? cursor[0] + nums.shift() : nums.shift();
          ensure();
          current.push([x, cursor[1]]);
          cursor = [x, cursor[1]];
          break;
        }
        case "V": {
          if (nums.length < 1) return;
          const y = isRel ? cursor[1] + nums.shift() : nums.shift();
          ensure();
          current.push([cursor[0], y]);
          cursor = [cursor[0], y];
          break;
        }
        case "Q": {
          if (nums.length < 4) return;
          const cx = isRel ? cursor[0] + nums.shift() : nums.shift();
          const cy = isRel ? cursor[1] + nums.shift() : nums.shift();
          const x = isRel ? cursor[0] + nums.shift() : nums.shift();
          const y = isRel ? cursor[1] + nums.shift() : nums.shift();
          ensure();
          flatQuad(cursor, [cx, cy], [x, y]);
          cursor = [x, y];
          break;
        }
        case "C": {
          if (nums.length < 6) return;
          const c1x = isRel ? cursor[0] + nums.shift() : nums.shift();
          const c1y = isRel ? cursor[1] + nums.shift() : nums.shift();
          const c2x = isRel ? cursor[0] + nums.shift() : nums.shift();
          const c2y = isRel ? cursor[1] + nums.shift() : nums.shift();
          const x = isRel ? cursor[0] + nums.shift() : nums.shift();
          const y = isRel ? cursor[1] + nums.shift() : nums.shift();
          ensure();
          flatCubic(cursor, [c1x, c1y], [c2x, c2y], [x, y]);
          cursor = [x, y];
          break;
        }
        default:
          nums = [];
          return;
      }
    }
  };
  for (const tok of tokens) {
    if (/^[A-Za-z]$/.test(tok)) {
      consume();
      cmd = tok;
      nums = [];
      if (cmd === "Z" || cmd === "z") consume();
      continue;
    }
    nums.push(parseFloat(tok));
    const upper = cmd.toUpperCase();
    if (upper === "M" || upper === "L") {
      if (nums.length >= 2) consume();
    } else if (upper === "H" || upper === "V") {
      if (nums.length >= 1) consume();
    } else if (upper === "Q") {
      if (nums.length >= 4) consume();
    } else if (upper === "C") {
      if (nums.length >= 6) consume();
    }
  }
  flush();
  return subpaths;
}

// src/email-builder/presets/subtrees/hero-centered-cta.ts
var heroCenteredCta = {
  key: "hero.centered-cta",
  version: "1.0.0",
  name: "Hero \u2014 centered headline + CTA",
  description: "Single-column hero with a centered eyebrow, headline, subhead, and primary CTA button. Works as an opener or a mid-email feature.",
  category: "hero",
  type: "subtree",
  metadata: {
    tags: ["hero", "centered", "cta", "text-focused", "minimal"],
    aestheticTier: "minimal"
  },
  subtree: {
    id: "preset-hero-centered-cta",
    type: "section",
    name: "Hero",
    props: {},
    style: {
      backgroundColor: "#FFFFFF",
      padding: { top: 56, right: 24, bottom: 56, left: 24 }
    },
    children: [
      {
        id: "preset-hero-centered-cta-col",
        type: "column",
        props: {},
        children: [
          {
            id: "preset-hero-centered-cta-eyebrow",
            type: "text",
            props: { content: "New this week", format: "plain", role: "eyebrow" },
            style: {
              fontSize: 12,
              fontWeight: 600,
              color: "#6B7280",
              align: "center",
              letterSpacing: 2,
              padding: { top: 0, right: 0, bottom: 8, left: 0 }
            }
          },
          {
            id: "preset-hero-centered-cta-headline",
            type: "text",
            props: { content: "Your headline goes here", format: "plain", role: "headline" },
            style: {
              fontSize: 36,
              fontWeight: 700,
              color: "#111827",
              align: "center",
              lineHeight: 1.15,
              padding: { top: 0, right: 0, bottom: 12, left: 0 }
            }
          },
          {
            id: "preset-hero-centered-cta-subhead",
            type: "text",
            props: { content: "A short sentence that sets up the value and invites the click.", format: "plain", role: "subheadline" },
            style: {
              fontSize: 16,
              fontWeight: 400,
              color: "#4B5563",
              align: "center",
              lineHeight: 1.5,
              padding: { top: 0, right: 0, bottom: 24, left: 0 }
            }
          },
          {
            id: "preset-hero-centered-cta-button",
            type: "button",
            props: { label: "Shop Now", href: "https://example.com", variant: "primary" },
            style: { align: "center" }
          }
        ]
      }
    ]
  }
};

// src/email-builder/presets/subtrees/hero-image-split.ts
var heroImageSplit = {
  key: "hero.image-split",
  version: "1.0.0",
  name: "Hero \u2014 image + copy split",
  description: "Two-column hero with a product or lifestyle image on one side and headline + CTA on the other. Stacks on mobile.",
  category: "hero",
  type: "subtree",
  metadata: {
    tags: ["hero", "split", "image", "two-column", "structured"],
    aestheticTier: "structured"
  },
  subtree: {
    id: "preset-hero-split",
    type: "section",
    name: "Hero",
    props: {},
    style: {
      backgroundColor: "#F9FAFB",
      padding: { top: 40, right: 24, bottom: 40, left: 24 }
    },
    children: [
      {
        id: "preset-hero-split-image-col",
        type: "column",
        props: { width: "50%", verticalAlign: "middle" },
        children: [
          {
            id: "preset-hero-split-image",
            type: "image",
            props: {
              src: "https://placehold.co/600x600/e5e7eb/6b7280?text=Image",
              alt: "Featured product"
            },
            style: { width: "100%", borderRadius: 8, fluidOnMobile: true }
          }
        ]
      },
      {
        id: "preset-hero-split-copy-col",
        type: "column",
        props: { width: "50%", verticalAlign: "middle" },
        children: [
          {
            id: "preset-hero-split-headline",
            type: "text",
            props: { content: "A better everyday", format: "plain", role: "headline" },
            style: {
              fontSize: 30,
              fontWeight: 700,
              color: "#111827",
              align: "left",
              lineHeight: 1.2,
              padding: { top: 0, right: 0, bottom: 10, left: 0 }
            }
          },
          {
            id: "preset-hero-split-subhead",
            type: "text",
            props: { content: "Short copy that pairs with the image.", format: "plain", role: "subheadline" },
            style: {
              fontSize: 15,
              color: "#4B5563",
              align: "left",
              lineHeight: 1.55,
              padding: { top: 0, right: 0, bottom: 18, left: 0 }
            }
          },
          {
            id: "preset-hero-split-button",
            type: "button",
            props: { label: "Shop the edit", href: "https://example.com", variant: "primary" },
            style: { align: "left" }
          }
        ]
      }
    ]
  }
};

// src/email-builder/presets/subtrees/hero-full-bleed-canvas.ts
var heroFullBleedCanvas = {
  key: "hero.full-bleed-canvas",
  version: "1.0.0",
  name: "Hero \u2014 full-bleed canvas composition",
  description: "A canvas-section hero for image-grade compositions that MJML structures can not express cleanly. Starts with an empty canvas; the canvas remix agent fills in objects.",
  category: "hero",
  type: "subtree",
  metadata: {
    tags: ["hero", "canvas", "full-bleed", "designed", "image-focused"],
    aestheticTier: "designed"
  },
  subtree: {
    id: "preset-hero-canvas",
    type: "canvas-section",
    name: "Canvas hero",
    props: {
      alt: "Hero composition",
      fallbackText: "See the latest collection",
      scene: { width: 600, height: 420, backgroundColor: "#111827" }
    },
    children: []
  }
};

// src/email-builder/presets/subtrees/product-grid-3.ts
var productGrid3 = {
  key: "product.grid-3",
  version: "1.0.0",
  name: "Products \u2014 3-column grid",
  description: "A 3-column product grid pulling from a product-feed source (top-sellers, recommended, or manual ids). Stacks to 1 column on mobile.",
  category: "product",
  type: "subtree",
  metadata: {
    tags: ["product", "grid", "three-column", "commerce", "structured"],
    aestheticTier: "structured"
  },
  subtree: {
    id: "preset-product-grid-3",
    type: "section",
    name: "Product grid",
    props: {},
    style: {
      backgroundColor: "#FFFFFF",
      padding: { top: 40, right: 16, bottom: 40, left: 16 }
    },
    // product-feed is a section-level child alongside the heading column —
    // ColumnNode.children is restricted to ContentNode, which doesn't include
    // product-feed. SectionNode.children accepts column | group | product-feed.
    children: [
      {
        id: "preset-product-grid-3-col",
        type: "column",
        props: {},
        children: [
          {
            id: "preset-product-grid-3-eyebrow",
            type: "text",
            props: { content: "Featured", format: "plain", role: "eyebrow" },
            style: {
              fontSize: 12,
              fontWeight: 600,
              color: "#6B7280",
              align: "center",
              letterSpacing: 2,
              padding: { top: 0, right: 0, bottom: 6, left: 0 }
            }
          },
          {
            id: "preset-product-grid-3-heading",
            type: "text",
            props: { content: "Shop the picks", format: "plain", role: "headline" },
            style: {
              fontSize: 24,
              fontWeight: 700,
              color: "#111827",
              align: "center",
              padding: { top: 0, right: 0, bottom: 20, left: 0 }
            }
          }
        ]
      },
      {
        id: "preset-product-grid-3-feed",
        type: "product-feed",
        props: {
          source: { kind: "top-sellers" },
          layout: "grid-3",
          limit: 3,
          fallback: "placeholder",
          itemTemplate: {
            include: ["image", "title", "price", "cta"],
            ctaLabel: "Shop",
            ctaVariant: "primary",
            imageAspectRatio: "4:5"
          }
        },
        style: { gap: 16 }
      }
    ]
  }
};

// src/email-builder/presets/subtrees/product-grid-2.ts
var productGrid2 = {
  key: "product.grid-2",
  version: "1.0.0",
  name: "Products \u2014 2-column grid",
  description: "A 2-column product grid with larger imagery. Use when you want the product photography to carry the section.",
  category: "product",
  type: "subtree",
  metadata: {
    tags: ["product", "grid", "two-column", "commerce", "styled"],
    aestheticTier: "styled"
  },
  subtree: {
    id: "preset-product-grid-2",
    type: "section",
    name: "Product grid",
    props: {},
    style: {
      backgroundColor: "#FFFFFF",
      padding: { top: 40, right: 16, bottom: 40, left: 16 }
    },
    // product-feed is a section-level child alongside the heading column —
    // ColumnNode.children is restricted to ContentNode, which doesn't include
    // product-feed. SectionNode.children accepts column | group | product-feed.
    children: [
      {
        id: "preset-product-grid-2-col",
        type: "column",
        props: {},
        children: [
          {
            id: "preset-product-grid-2-heading",
            type: "text",
            props: { content: "The edit", format: "plain", role: "headline" },
            style: {
              fontSize: 26,
              fontWeight: 700,
              color: "#111827",
              align: "center",
              padding: { top: 0, right: 0, bottom: 20, left: 0 }
            }
          }
        ]
      },
      {
        id: "preset-product-grid-2-feed",
        type: "product-feed",
        props: {
          source: { kind: "recommended" },
          layout: "grid-2",
          limit: 2,
          fallback: "placeholder",
          itemTemplate: {
            include: ["image", "title", "price", "compareAtPrice", "cta"],
            ctaLabel: "Shop",
            ctaVariant: "primary",
            imageAspectRatio: "1:1"
          }
        },
        style: { gap: 20 }
      }
    ]
  }
};

// src/email-builder/presets/subtrees/cta-banner.ts
var ctaBanner = {
  key: "cta.banner",
  version: "1.0.0",
  name: "CTA banner \u2014 centered prompt",
  description: "A dense, high-contrast banner with a short prompt and a primary CTA. Use as a section break or as the closing push.",
  category: "cta",
  type: "subtree",
  metadata: {
    tags: ["cta", "banner", "high-contrast", "styled"],
    aestheticTier: "styled"
  },
  subtree: {
    id: "preset-cta-banner",
    type: "section",
    name: "CTA banner",
    props: {},
    style: {
      backgroundColor: "#111827",
      padding: { top: 36, right: 24, bottom: 36, left: 24 }
    },
    children: [
      {
        id: "preset-cta-banner-col",
        type: "column",
        props: {},
        children: [
          {
            id: "preset-cta-banner-headline",
            type: "text",
            props: { content: "Ready to upgrade?", format: "plain", role: "headline" },
            style: {
              fontSize: 22,
              fontWeight: 700,
              color: "#FFFFFF",
              align: "center",
              padding: { top: 0, right: 0, bottom: 8, left: 0 }
            }
          },
          {
            id: "preset-cta-banner-sub",
            type: "text",
            props: { content: "Get 15% off your first order.", format: "plain", role: "subheadline" },
            style: {
              fontSize: 14,
              color: "#D1D5DB",
              align: "center",
              padding: { top: 0, right: 0, bottom: 18, left: 0 }
            }
          },
          {
            id: "preset-cta-banner-button",
            type: "button",
            props: { label: "Claim offer", href: "https://example.com", variant: "primary" },
            style: {
              backgroundColor: "#FFFFFF",
              color: "#111827",
              align: "center"
            }
          }
        ]
      }
    ]
  }
};

// src/email-builder/presets/subtrees/content-text-block.ts
var contentTextBlock = {
  key: "content.text-block",
  version: "1.0.0",
  name: "Content \u2014 editorial text block",
  description: "A clean, centered editorial block \u2014 headline, long copy paragraph, and an inline link via a button. Use for letters, announcements, or brand story beats.",
  category: "content",
  type: "subtree",
  metadata: {
    tags: ["content", "editorial", "text", "minimal"],
    aestheticTier: "minimal"
  },
  subtree: {
    id: "preset-content-text-block",
    type: "section",
    name: "Editorial block",
    props: {},
    style: {
      backgroundColor: "#FFFFFF",
      padding: { top: 40, right: 32, bottom: 40, left: 32 }
    },
    children: [
      {
        id: "preset-content-text-col",
        type: "column",
        props: {},
        children: [
          {
            id: "preset-content-text-headline",
            type: "text",
            props: { content: "A note from the team", format: "plain", role: "headline" },
            style: {
              fontSize: 24,
              fontWeight: 700,
              color: "#111827",
              align: "center",
              padding: { top: 0, right: 0, bottom: 14, left: 0 }
            }
          },
          {
            id: "preset-content-text-body",
            type: "text",
            props: {
              content: "This is a long-form paragraph. Replace it with whatever you want to share with your audience \u2014 a letter, a product story, a seasonal note.",
              format: "plain",
              role: "body"
            },
            style: {
              fontSize: 15,
              color: "#374151",
              align: "left",
              lineHeight: 1.7,
              padding: { top: 0, right: 0, bottom: 18, left: 0 }
            }
          },
          {
            id: "preset-content-text-link",
            type: "button",
            props: { label: "Keep reading", href: "https://example.com", variant: "ghost" },
            style: { align: "center" }
          }
        ]
      }
    ]
  }
};

// src/email-builder/presets/subtrees/footer-social-nav.ts
var footerSocialNav = {
  key: "footer.social-nav",
  version: "1.0.0",
  name: "Footer \u2014 social + legal",
  description: "Standard brand footer with social icons, an unsubscribe + privacy row, and a company address line. Uses system tokens for link colors.",
  category: "footer",
  type: "subtree",
  metadata: {
    tags: ["footer", "social", "legal", "standard"],
    aestheticTier: "minimal"
  },
  subtree: {
    id: "preset-footer-social-nav",
    type: "section",
    name: "Footer",
    props: {},
    meta: { aiHints: { role: "footer" } },
    style: {
      backgroundColor: "#F9FAFB",
      padding: { top: 24, right: 24, bottom: 24, left: 24 }
    },
    children: [
      {
        id: "preset-footer-col",
        type: "column",
        props: {},
        children: [
          {
            id: "preset-footer-social",
            type: "social",
            props: {
              links: [
                { platform: "instagram", href: "$brand.social.instagram" },
                { platform: "tiktok", href: "$brand.social.tiktok" },
                { platform: "facebook", href: "$brand.social.facebook" }
              ],
              iconStyle: "outline"
            },
            style: { align: "center", iconSize: 22, padding: { top: 0, right: 0, bottom: 12, left: 0 } }
          },
          {
            id: "preset-footer-legal-links",
            type: "text",
            props: {
              content: '<a href="[NM:UNSUBSCRIBE_URL]" style="color:#6B7280;">Unsubscribe</a> &nbsp;\xB7&nbsp; <a href="[NM:PRIVACY_URL]" style="color:#6B7280;">Privacy</a>',
              format: "html",
              role: "legal"
            },
            style: { align: "center", fontSize: 12, color: "#6B7280" }
          },
          {
            id: "preset-footer-address",
            type: "text",
            props: { content: "[NM:COMPANY_ADDRESS]", format: "plain", role: "legal" },
            style: {
              align: "center",
              fontSize: 11,
              color: "#9CA3AF",
              padding: { top: 6, right: 0, bottom: 0, left: 0 }
            }
          }
        ]
      }
    ]
  }
};

// src/email-builder/presets/subtrees/header-centered-logo.ts
var headerCenteredLogo = {
  key: "header.centered-logo",
  version: "1.0.0",
  name: "Header \u2014 centered logo",
  description: "Minimal header with a centered brand logo and a thin bottom border. Use for clean, modern brand voices.",
  category: "header",
  type: "subtree",
  metadata: {
    tags: ["header", "logo", "minimal", "centered"],
    aestheticTier: "minimal"
  },
  subtree: {
    id: "preset-header-centered-logo",
    type: "section",
    name: "Header",
    props: {},
    meta: { aiHints: { role: "header" } },
    style: {
      backgroundColor: "#FFFFFF",
      padding: { top: 16, right: 24, bottom: 16, left: 24 },
      borderBottom: "1px solid #E5E7EB"
    },
    children: [
      {
        id: "preset-header-col",
        type: "column",
        props: {},
        children: [
          {
            id: "preset-header-logo",
            type: "image",
            props: {
              src: { $brandAsset: "logo" },
              alt: "Logo",
              href: "https://example.com",
              variant: "logo"
            },
            style: { width: 120, align: "center" }
          }
        ]
      }
    ]
  }
};

// src/email-builder/presets/subtrees/email-welcome-v1.ts
var emailWelcomeV1 = {
  key: "email.welcome-v1",
  version: "1.0.0",
  name: "Email \u2014 welcome flow (v1)",
  description: "A complete starter welcome email: centered logo header, centered-CTA hero, 3-product grid, CTA banner, and social-nav footer.",
  category: "full-email",
  type: "document",
  metadata: {
    tags: ["welcome", "onboarding", "commerce", "full-email"],
    aestheticTier: "structured"
  },
  subtree: {
    schemaVersion: 4,
    meta: {
      createdAt: "1970-01-01T00:00:00.000Z",
      updatedAt: "1970-01-01T00:00:00.000Z",
      authorId: "preset:email.welcome-v1",
      sourcePresetKey: "email.welcome-v1",
      version: 0
    },
    root: {
      id: "preset-email-welcome-v1",
      type: "email",
      props: { width: 600, darkModeStrategy: "none" },
      style: {
        backgroundColor: "#F3F4F6",
        textColor: "#111827",
        fontFamily: "Helvetica, Arial, sans-serif"
      },
      children: [
        {
          id: "preset-email-welcome-v1-head",
          type: "head",
          props: { title: "Welcome", previewText: "A short intro to your brand." }
        },
        headerCenteredLogo.subtree,
        heroCenteredCta.subtree,
        productGrid3.subtree,
        ctaBanner.subtree,
        footerSocialNav.subtree
      ]
    }
  }
};

// src/email-builder/presets/index.ts
var PRESET_CATALOG = [
  heroCenteredCta,
  heroImageSplit,
  heroFullBleedCanvas,
  productGrid3,
  productGrid2,
  ctaBanner,
  contentTextBlock,
  footerSocialNav,
  headerCenteredLogo,
  emailWelcomeV1
];
function findPreset(key) {
  return PRESET_CATALOG.find((p) => p.key === key);
}
function filterPresets(opts) {
  const query = opts.query?.toLowerCase().trim();
  const tokens = query ? query.split(/\s+/).filter((t) => t.length > 1) : [];
  return PRESET_CATALOG.filter((preset) => {
    if (opts.category && preset.category !== opts.category) return false;
    if (opts.tags?.length && !opts.tags.some((t) => preset.metadata.tags.includes(t))) return false;
    if (tokens.length) {
      const haystack = [
        preset.name,
        preset.description,
        preset.category,
        preset.metadata.aestheticTier ?? "",
        ...preset.metadata.tags
      ].join(" ").toLowerCase();
      if (!tokens.every((tok) => haystack.includes(tok))) return false;
    }
    return true;
  });
}

// src/email-builder/diagnostics/walk.ts
var MERGE_TAG_RE = /\{\{\s*[\w.$-]+[^}]*\}\}/;
function getChildren(node) {
  if ("children" in node && Array.isArray(node.children)) {
    return node.children;
  }
  return [];
}
function walkNodes(doc, visit) {
  const go = (node, parent) => {
    visit(node, parent);
    for (const child of getChildren(node)) go(child, node);
  };
  go(doc.root, null);
}
function findDescendants(node, predicate) {
  const out = [];
  const go = (n) => {
    if (predicate(n)) out.push(n);
    for (const child of getChildren(n)) go(child);
  };
  for (const child of getChildren(node)) go(child);
  return out;
}
function isSectionNode(node) {
  return node.type === "section";
}
function isRasterizeCandidate(section) {
  const explicit = section.props?.turnIntoImage;
  if (explicit === true) return true;
  if (explicit === false) return false;
  return Boolean(section.style?.backgroundImage || section.style?.backgroundGradient);
}
function isRasterizeMarked(node) {
  return (node.type === "section" || node.type === "group" || node.type === "column") && node.props.turnIntoImage === true;
}
function findAncestorChain(doc, targetNodeId) {
  let result = null;
  const recurse = (node, chain) => {
    if (node.id === targetNodeId) {
      result = chain;
      return true;
    }
    for (const child of getChildren(node)) {
      if (recurse(child, [...chain, node])) return true;
    }
    return false;
  };
  recurse(doc.root, []);
  return result;
}
function findOutermostRasterizeNodes(doc) {
  const out = [];
  const recurse = (node, hasMarkedAncestor) => {
    const marked = isRasterizeMarked(node);
    if (marked && !hasMarkedAncestor) out.push(node);
    const inMarkedSubtree = hasMarkedAncestor || marked;
    for (const child of getChildren(node)) recurse(child, inMarkedSubtree);
  };
  recurse(doc.root, false);
  return out;
}
function collectInteractiveDescendants(section) {
  const out = [];
  for (const d of findDescendants(section, () => true)) {
    if (d.type === "button") {
      out.push({
        nodeId: d.id,
        nodeType: d.type,
        href: d.props.href,
        label: d.props.label ?? "Button"
      });
    } else if (d.type === "image") {
      const href = d.props.href;
      if (href) {
        out.push({ nodeId: d.id, nodeType: d.type, href, label: "Image link" });
      }
    } else if (d.type === "product-feed") {
      out.push({
        nodeId: d.id,
        nodeType: d.type,
        href: void 0,
        label: "Product feed (dynamic per-product links)"
      });
    } else if (d.type === "navbar" || d.type === "social") {
      out.push({ nodeId: d.id, nodeType: d.type, href: void 0, label: d.type });
    }
  }
  return out;
}
function containsMergeTags(section) {
  for (const d of findDescendants(section, () => true)) {
    const p = d.props;
    for (const value of Object.values(p)) {
      if (typeof value === "string" && MERGE_TAG_RE.test(value)) return true;
    }
  }
  return false;
}
function hasProductFeed(section) {
  return findDescendants(section, (n) => n.type === "product-feed").length > 0;
}

// src/email-builder/diagnostics/registry.ts
var CheckerRegistry = class {
  constructor() {
    this.checkers = /* @__PURE__ */ new Map();
  }
  register(checker) {
    if (this.checkers.has(checker.code)) {
      throw new Error(`Duplicate checker code: ${checker.code}`);
    }
    this.checkers.set(checker.code, checker);
  }
  get(code) {
    return this.checkers.get(code);
  }
  list() {
    return Array.from(this.checkers.values());
  }
};
var defaultRegistry = new CheckerRegistry();

// src/email-builder/diagnostics/analyze.ts
function readResolutions(doc) {
  return doc.diagnosticResolutions ?? {};
}
function buildAnalyzerContext(doc) {
  const resolutions = readResolutions(doc);
  return {
    document: doc,
    getResolution: (nodeId, code) => {
      const entries = resolutions[nodeId];
      if (!entries) return void 0;
      return entries.find((e) => e.code === code)?.resolution;
    }
  };
}
function analyze(doc, options = {}, checkers = defaultRegistry.list()) {
  const active = checkers.filter((c) => {
    if (options.include && !options.include.includes(c.code)) return false;
    if (options.exclude && options.exclude.includes(c.code)) return false;
    return true;
  });
  const ctx = buildAnalyzerContext(doc);
  const out = [];
  for (const checker of active) {
    try {
      for (const diag of checker.run(ctx)) out.push(diag);
    } catch (err) {
      out.push({
        code: "analyzer-internal-error",
        severity: "error",
        nodeId: doc.root.id,
        message: `Checker ${checker.code} threw: ${err instanceof Error ? err.message : String(err)}`
      });
    }
  }
  const sevWeight = { error: 0, warning: 1, info: 2 };
  out.sort((a, b) => {
    if (a.nodeId !== b.nodeId) return a.nodeId < b.nodeId ? -1 : 1;
    if (a.severity !== b.severity) return sevWeight[a.severity] - sevWeight[b.severity];
    return a.code < b.code ? -1 : a.code > b.code ? 1 : 0;
  });
  return out;
}
function groupByNodeId(diagnostics) {
  var _a;
  const out = {};
  for (const d of diagnostics) {
    (out[_a = d.nodeId] ?? (out[_a] = [])).push(d);
  }
  return out;
}
function highestSeverity(diagnostics) {
  if (diagnostics.some((d) => d.severity === "error")) return "error";
  if (diagnostics.some((d) => d.severity === "warning")) return "warning";
  if (diagnostics.some((d) => d.severity === "info")) return "info";
  return null;
}

// src/email-builder/diagnostics/checkers/rasterize-link-conflict.ts
var RASTERIZE_LINK_CONFLICT = "rasterize-link-conflict";
var rasterizeLinkConflictChecker = {
  code: RASTERIZE_LINK_CONFLICT,
  description: "Section has bg-image/gradient and multiple interactive descendants; rasterizing would drop per-element links.",
  defaultSeverity: "warning",
  run(ctx) {
    const out = [];
    walkNodes(ctx.document, (node) => {
      if (!isSectionNode(node)) return;
      if (!isRasterizeCandidate(node)) return;
      const interactive = collectInteractiveDescendants(node);
      const productFeed = hasProductFeed(node);
      const resolved = ctx.getResolution(node.id, RASTERIZE_LINK_CONFLICT);
      if (productFeed) {
        const resolutions2 = [
          {
            id: "keep-html",
            label: "Keep as live HTML (preserve product links)",
            preferred: true,
            mutate: { kind: "setDiagnosticResolution", nodeId: node.id, code: RASTERIZE_LINK_CONFLICT, resolution: "keep-html" }
          },
          {
            id: "rasterize-decorative",
            label: "Rasterize anyway (lose all product links)",
            mutate: { kind: "setDiagnosticResolution", nodeId: node.id, code: RASTERIZE_LINK_CONFLICT, resolution: "rasterize-decorative" }
          }
        ];
        out.push({
          code: RASTERIZE_LINK_CONFLICT,
          severity: resolved ? "info" : "error",
          nodeId: node.id,
          message: resolved ? `Resolved: ${resolved}. Product feed + rasterized section.` : "This section has a product feed. Rasterizing would make all product links uniform and lose per-product targeting.",
          resolutions: resolutions2,
          context: { reason: "product-feed", resolved }
        });
        return;
      }
      if (interactive.length === 0) return;
      if (interactive.length === 1) {
        const el = interactive[0];
        const sectionHref = node.props.href;
        if (sectionHref && el.href && sectionHref !== el.href) {
          out.push({
            code: RASTERIZE_LINK_CONFLICT,
            severity: resolved ? "info" : "warning",
            nodeId: node.id,
            message: resolved ? `Resolved: ${resolved}.` : `Section href (${sectionHref}) differs from child link (${el.href}). Only one will apply after rasterize.`,
            resolutions: [
              {
                id: "keep-section-href",
                label: "Use section link",
                preferred: true,
                mutate: { kind: "setDiagnosticResolution", nodeId: node.id, code: RASTERIZE_LINK_CONFLICT, resolution: "keep-section-href" }
              },
              {
                id: `promote:${el.nodeId}`,
                label: `Use child link (${el.label})`,
                mutate: { kind: "setSectionHref", nodeId: node.id, href: el.href ?? null }
              },
              {
                id: "keep-html",
                label: "Keep as live HTML",
                mutate: { kind: "setDiagnosticResolution", nodeId: node.id, code: RASTERIZE_LINK_CONFLICT, resolution: "keep-html" }
              }
            ],
            context: { resolved, interactive }
          });
        }
        return;
      }
      const resolutions = [
        {
          id: "keep-html",
          label: "Keep as live HTML (preserve all links)",
          preferred: true,
          mutate: { kind: "setDiagnosticResolution", nodeId: node.id, code: RASTERIZE_LINK_CONFLICT, resolution: "keep-html" }
        },
        ...interactive.filter((el) => el.href).map((el) => ({
          id: `promote:${el.nodeId}`,
          label: `Rasterize \u2014 use ${el.label} link`,
          mutate: { kind: "setSectionHref", nodeId: node.id, href: el.href ?? null }
        })),
        {
          id: "rasterize-decorative",
          label: "Rasterize with no link (decorative)",
          mutate: { kind: "setDiagnosticResolution", nodeId: node.id, code: RASTERIZE_LINK_CONFLICT, resolution: "rasterize-decorative" }
        }
      ];
      out.push({
        code: RASTERIZE_LINK_CONFLICT,
        severity: resolved ? "info" : "warning",
        nodeId: node.id,
        message: resolved ? `Resolved: ${resolved}.` : `This section has ${interactive.length} clickable elements. Rasterizing will keep at most one link.`,
        resolutions,
        context: { resolved, interactive }
      });
    });
    return out;
  }
};

// src/email-builder/diagnostics/checkers/rasterize-contains-merge-tags.ts
var RASTERIZE_CONTAINS_MERGE_TAGS = "rasterize-contains-merge-tags";
var rasterizeContainsMergeTagsChecker = {
  code: RASTERIZE_CONTAINS_MERGE_TAGS,
  description: "Section has bg-image/gradient and contains merge tags \u2014 cannot be pre-rasterized.",
  defaultSeverity: "error",
  run(ctx) {
    const out = [];
    walkNodes(ctx.document, (node) => {
      if (!isSectionNode(node)) return;
      if (!isRasterizeCandidate(node)) return;
      if (!containsMergeTags(node)) return;
      out.push({
        code: RASTERIZE_CONTAINS_MERGE_TAGS,
        severity: "error",
        nodeId: node.id,
        message: "This section contains merge tags and a background image/gradient. Rasterizing would bake personalization into the PNG. The section will render as live HTML instead.",
        resolutions: [
          {
            id: "acknowledge",
            label: "OK, keep as live HTML",
            preferred: true,
            mutate: {
              kind: "setDiagnosticResolution",
              nodeId: node.id,
              code: RASTERIZE_CONTAINS_MERGE_TAGS,
              resolution: "acknowledge"
            }
          }
        ]
      });
    });
    return out;
  }
};

// src/email-builder/diagnostics/rasterize-plan.ts
function buildRasterizePlan(doc) {
  const diagnostics = analyze(doc);
  const mergeTagBlocked = new Set(
    diagnostics.filter((d) => d.code === RASTERIZE_CONTAINS_MERGE_TAGS).map((d) => d.nodeId)
  );
  const resolutions = doc.diagnosticResolutions ?? {};
  const getRes = (nodeId, code) => resolutions[nodeId]?.find((r) => r.code === code)?.resolution;
  const outermost = findOutermostRasterizeNodes(doc);
  const plan = [];
  const sectionsCovered = /* @__PURE__ */ new Set();
  for (const node of outermost) {
    if (node.type === "section") {
      if (mergeTagBlocked.has(node.id)) continue;
      const linkConflictResolution = getRes(node.id, RASTERIZE_LINK_CONFLICT);
      const hasConflict = diagnostics.some(
        (d) => d.nodeId === node.id && d.code === RASTERIZE_LINK_CONFLICT && d.severity !== "info"
      );
      if (hasConflict && linkConflictResolution === "keep-html") continue;
      if (hasConflict && linkConflictResolution === void 0) continue;
      plan.push({ node, resolution: linkConflictResolution });
      sectionsCovered.add(node.id);
    } else {
      if (mergeTagBlocked.has(node.id)) continue;
      plan.push({ node, resolution: void 0 });
    }
  }
  walkNodes(doc, (node) => {
    if (!isSectionNode(node)) return;
    if (sectionsCovered.has(node.id)) return;
    if (!isRasterizeCandidate(node)) return;
    if (node.props.turnIntoImage === true) return;
    if (mergeTagBlocked.has(node.id)) return;
    const linkConflictResolution = getRes(node.id, RASTERIZE_LINK_CONFLICT);
    const hasConflict = diagnostics.some(
      (d) => d.nodeId === node.id && d.code === RASTERIZE_LINK_CONFLICT && d.severity !== "info"
    );
    if (hasConflict && linkConflictResolution === "keep-html") return;
    if (hasConflict && linkConflictResolution === void 0) return;
    plan.push({ node, resolution: linkConflictResolution });
  });
  return plan;
}
function isSectionTarget(target) {
  return target.node.type === "section";
}

// src/email-builder/diagnostics/checkers/rasterize-missing-alt-text.ts
var RASTERIZE_MISSING_ALT_TEXT = "rasterize-missing-alt-text";
var rasterizeMissingAltTextChecker = {
  code: RASTERIZE_MISSING_ALT_TEXT,
  description: "Rasterize-eligible section is missing alt text for its background image.",
  defaultSeverity: "warning",
  run(ctx) {
    const out = [];
    walkNodes(ctx.document, (node) => {
      if (!isSectionNode(node)) return;
      if (!isRasterizeCandidate(node)) return;
      const alt = node.style?.backgroundImageAlt;
      if (alt && alt.trim().length > 0) return;
      out.push({
        code: RASTERIZE_MISSING_ALT_TEXT,
        severity: "warning",
        nodeId: node.id,
        message: "This section has a background image or gradient but no alt text. Add a description so screen readers and image-blocked clients can surface the content.",
        resolutions: [
          {
            id: "dismiss",
            label: "Ignore for this section",
            mutate: {
              kind: "setDiagnosticResolution",
              nodeId: node.id,
              code: RASTERIZE_MISSING_ALT_TEXT,
              resolution: "dismiss"
            }
          }
        ]
      });
    });
    return out;
  }
};

// src/email-builder/diagnostics/checkers/rasterize-missing-href.ts
var RASTERIZE_MISSING_HREF = "rasterize-missing-href";
var rasterizeMissingHrefChecker = {
  code: RASTERIZE_MISSING_HREF,
  description: "Rasterize-eligible section has no href and no interactive children \u2014 likely missing a link.",
  defaultSeverity: "info",
  run(ctx) {
    const out = [];
    walkNodes(ctx.document, (node) => {
      if (!isSectionNode(node)) return;
      if (!isRasterizeCandidate(node)) return;
      const sectionHref = node.props.href;
      if (sectionHref && sectionHref.trim().length > 0) return;
      const interactive = collectInteractiveDescendants(node);
      if (interactive.length > 0) return;
      out.push({
        code: RASTERIZE_MISSING_HREF,
        severity: "info",
        nodeId: node.id,
        message: "This hero-style section has no click target. If it should drive traffic, add a Link in the Layout panel.",
        resolutions: [
          {
            id: "decorative",
            label: "Decorative \u2014 no link needed",
            mutate: {
              kind: "setDiagnosticResolution",
              nodeId: node.id,
              code: RASTERIZE_MISSING_HREF,
              resolution: "decorative"
            }
          }
        ]
      });
    });
    return out;
  }
};

// src/email-builder/diagnostics/index.ts
defaultRegistry.register(rasterizeLinkConflictChecker);
defaultRegistry.register(rasterizeContainsMergeTagsChecker);
defaultRegistry.register(rasterizeMissingAltTextChecker);
defaultRegistry.register(rasterizeMissingHrefChecker);

// src/email-builder/compiler/util/color.ts
function gradientToCss(g) {
  const stops = g.stops.map((s) => `${s.color} ${Math.round(s.position)}%`).join(", ");
  return `linear-gradient(${Math.round(g.angle)}deg, ${stops})`;
}
function overlayToCss(o) {
  if (o.gradient) {
    const stops = o.gradient.stops.map((s) => `${hexToRgba2(s.color, o.opacity)} ${Math.round(s.position)}%`).join(", ");
    return `linear-gradient(${Math.round(o.gradient.angle)}deg, ${stops})`;
  }
  const rgba = hexToRgba2(o.color, o.opacity);
  return `linear-gradient(0deg, ${rgba} 0%, ${rgba} 100%)`;
}
function gradientFirstStop(g) {
  if (!g || g.stops.length === 0) return void 0;
  return g.stops[0].color;
}
function softenHex(hex, t) {
  const parsed = parseHex(hex);
  if (!parsed) return hex;
  const mix = (channel) => Math.round(channel * (1 - t) + 255 * t);
  const r = mix(parsed.r);
  const g = mix(parsed.g);
  const b = mix(parsed.b);
  return "#" + [r, g, b].map((n) => n.toString(16).padStart(2, "0")).join("");
}
function parseHex(hex) {
  if (typeof hex !== "string" || !hex.startsWith("#")) return null;
  let v = hex.slice(1).toLowerCase();
  if (v.length === 3) v = v.split("").map((c) => c + c).join("");
  if (v.length !== 6 || /[^0-9a-f]/.test(v)) return null;
  return {
    r: parseInt(v.slice(0, 2), 16),
    g: parseInt(v.slice(2, 4), 16),
    b: parseInt(v.slice(4, 6), 16)
  };
}
function hexToRgba2(color, alpha) {
  const a = Math.max(0, Math.min(1, alpha));
  const hex = color.trim().replace(/^#/, "");
  const full = hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) return color;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

// src/email-builder/compiler/util/layout-analyze.ts
function computeGroupMinHeight(children) {
  let max = 0;
  for (const child of children) {
    if (child.type !== "column") continue;
    const col = child;
    for (const grand of col.children) {
      if (grand.type === "image" && typeof grand.style?.height === "number") {
        max = Math.max(max, grand.style.height);
      }
    }
  }
  return max;
}
function computeColumnWidths(children, availableWidth) {
  const columns = children.map((c) => c.type === "column" ? c : null);
  let fixedWidthTotal = 0;
  let flexCount = 0;
  for (const c of columns) {
    const w = c?.props.width;
    if (!w) {
      flexCount++;
      continue;
    }
    if (w.endsWith("%")) {
      fixedWidthTotal += parseFloat(w) / 100 * availableWidth;
    } else if (w.endsWith("px")) {
      fixedWidthTotal += parseFloat(w);
    } else {
      flexCount++;
    }
  }
  const flexShare = flexCount > 0 ? Math.max(0, (availableWidth - fixedWidthTotal) / flexCount) : 0;
  const flexBase = Math.floor(flexShare);
  let flexRemainder = flexCount > 0 ? Math.floor(Math.max(0, availableWidth - fixedWidthTotal) - flexBase * flexCount) : 0;
  const nextFlexWidth = () => {
    const extra = flexRemainder > 0 ? 1 : 0;
    if (flexRemainder > 0) flexRemainder--;
    return flexBase + extra;
  };
  return children.map((child, i) => {
    const c = columns[i];
    if (!c) return availableWidth;
    const w = c.props.width;
    if (!w) return nextFlexWidth();
    if (w.endsWith("%")) return Math.floor(parseFloat(w) / 100 * availableWidth);
    if (w.endsWith("px")) return Math.floor(parseFloat(w));
    return nextFlexWidth();
  });
}
function wrapSemanticText(content, role) {
  const reset = "margin:0;font-size:inherit;font-weight:inherit;line-height:inherit;font-family:inherit;";
  switch (role) {
    case "headline":
      return `<h1 style="${reset}">${content}</h1>`;
    case "subheadline":
      return `<h2 style="${reset}">${content}</h2>`;
    default:
      return `<p style="margin:0;">${content}</p>`;
  }
}
function getBbox(obj) {
  const t = obj.props.transform;
  return { x: t.x, y: t.y, w: t.width, h: t.height };
}
function bboxesOverlap(a, b) {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}
function analyzeLayout(children, sceneWidth) {
  if (children.length === 0) return "raster";
  const bboxes = children.map(getBbox);
  for (const obj of children) {
    if (obj.props.transform.rotation && obj.props.transform.rotation !== 0) return "raster";
  }
  for (let i = 0; i < bboxes.length; i++) {
    for (let j = i + 1; j < bboxes.length; j++) {
      if (bboxesOverlap(bboxes[i], bboxes[j])) return "raster";
    }
  }
  const textNodes = children.filter((c) => c.type === "canvas-text");
  const visualNodes = children.filter((c) => c.type !== "canvas-text");
  if (visualNodes.length >= 3) {
    const xs = visualNodes.map((n) => n.props.transform.x).sort((a, b) => a - b);
    const gaps = xs.slice(1).map((x, i) => x - xs[i]);
    const avgGap = gaps.reduce((s, g) => s + g, 0) / gaps.length;
    const regular = gaps.every((g) => Math.abs(g - avgGap) < 20);
    if (regular && gaps.length >= 2) return "product-grid";
  }
  const wideTextNodes = textNodes.filter((n) => n.props.transform.width >= sceneWidth * 0.5);
  if (visualNodes.length > 0 && wideTextNodes.length > 0) return "hybrid";
  if (visualNodes.length === 0 && textNodes.length > 0) return "mjml-columns";
  return "raster";
}

// src/email-builder/compiler/util/image.ts
var ASPECT_RATIO_MAP = {
  "1:1": [1, 1],
  "16:9": [16, 9],
  "4:3": [4, 3],
  "3:4": [3, 4],
  "4:5": [4, 5],
  "2:1": [2, 1],
  "3:2": [3, 2],
  "1:2": [1, 2]
};
function heightFromAspectRatio(width, ratio) {
  const [w, h] = ASPECT_RATIO_MAP[ratio];
  return Math.round(width * h / w);
}
function isGifUrl(src) {
  try {
    const url = new URL(src);
    return /\.gif(\?|$)/i.test(url.pathname);
  } catch {
    return false;
  }
}

// src/email-builder/compiler/renderers/image.ts
async function resolveImage(node, ctx, brand) {
  const widthHint = typeof node.style?.width === "number" ? node.style.width : void 0;
  const hints = { width: widthHint };
  if (node.props.productId) {
    hints.productId = node.props.productId;
    hints.transparent = true;
  }
  if (node.props.variant === "logo") {
    const src = brand.assets.logo;
    if (!src) {
      ctx.logger.warn(
        `brand logo missing on node ${node.id}; rendering brand name as logotype (brand=${brand.name})`
      );
      return { kind: "brand-logotype", label: brand.name || "Logo" };
    }
    return {
      kind: "url",
      src: await ctx.resolveImage(src, { ...hints, transparent: true })
    };
  }
  if (typeof node.props.src === "object" && "$brandAsset" in node.props.src) {
    const assetKey = node.props.src.$brandAsset;
    const src = brand.assets[assetKey];
    if (!src) {
      ctx.logger.warn(
        `brand asset "${assetKey}" missing on node ${node.id}; rendering brand name as fallback`
      );
      return { kind: "brand-logotype", label: brand.name || assetKey };
    }
    return {
      kind: "url",
      src: await ctx.resolveImage(src, hints)
    };
  }
  return {
    kind: "url",
    src: await ctx.resolveImage(node.props.src, hints)
  };
}

// src/email-builder/compiler/renderers/product-feed.ts
async function compileProductFeed(node, ctx, sectionInnerWidth) {
  const items = await resolveProductItems(node, ctx);
  if (!items) {
    if (node.props.fallback === "hide") return [];
    if (node.props.fallback === "placeholder") {
      return [{ tagName: "mj-column", children: [{ tagName: "mj-text", content: "No products available." }] }];
    }
    throw {
      kind: "resolution",
      source: "product-feed",
      retryable: false,
      cause: "Product feed resolved to zero items.",
      nodeId: node.id
    };
  }
  const perRow = node.props.layout === "grid-3" ? 3 : node.props.layout === "grid-2" ? 2 : 1;
  const cardBg = asString(node.style?.cardBackgroundColor);
  if (perRow === 1) {
    const colWidthPx2 = sectionInnerWidth;
    const stacked = (await Promise.all(
      items.map(
        (item) => buildProductColumnContent(item, node.props.itemTemplate, ctx.theme, colWidthPx2, ctx, node.style)
      )
    )).flat();
    return [
      {
        tagName: "mj-column",
        attributes: compact({ "background-color": cardBg, padding: "0px" }),
        children: stacked
      }
    ];
  }
  const row = items.slice(0, perRow);
  const colWidthPct = `${(100 / perRow).toFixed(4)}%`;
  const colWidthPx = Math.floor(sectionInnerWidth / perRow);
  return Promise.all(
    row.map(async (item) => ({
      tagName: "mj-column",
      attributes: compact({ width: colWidthPct, "background-color": cardBg, padding: "0px" }),
      children: await buildProductColumnContent(item, node.props.itemTemplate, ctx.theme, colWidthPx, ctx, node.style)
    }))
  );
}
async function resolveProductItems(node, ctx) {
  const items = await ctx.resolveProductFeed(node.props.source, {
    limit: node.props.limit,
    sort: node.props.sort
  });
  if (items.length === 0) return null;
  return items;
}
async function buildProductColumnContent(item, template, theme, colWidthPx, ctx, feedStyle) {
  const include = new Set(template.include);
  const nodes = [];
  const textAlign = feedStyle?.textAlign ?? "left";
  const buttonAlign = feedStyle?.buttonAlign ?? "left";
  const isBlank = item.id.startsWith(BLANK_PRODUCT_ID);
  if (include.has("image") && (item.imageUrl || isBlank)) {
    if (isBlank) {
      const aspect = template.imageAspectRatio ?? "1:1";
      const targetHeight = heightFromAspectRatio(colWidthPx, aspect);
      nodes.push({
        tagName: "mj-text",
        attributes: compact({ padding: "0px" }),
        content: `<div style="width:100%;height:${targetHeight}px;background-color:#E5E7EB;"></div>`
      });
    } else if (item.imageUrl) {
      const aspect = template.imageAspectRatio ?? "1:1";
      const targetHeight = heightFromAspectRatio(colWidthPx, aspect);
      let imageSrc2 = item.imageUrl;
      if (template.productCutout && item.productId) {
        imageSrc2 = await ctx.resolveImage(imageSrc2, {
          width: colWidthPx,
          productId: item.productId,
          transparent: true
        });
      } else if (!isGifUrl(imageSrc2)) {
        try {
          const fitted = await ctx.resolveFittedImage({
            imageUrl: imageSrc2,
            width: colWidthPx,
            height: targetHeight,
            mode: "cover"
          });
          imageSrc2 = fitted.url;
        } catch (err) {
          ctx.logger.warn(`product-fit failed for ${imageSrc2}: ${err.message}`);
        }
      }
      nodes.push({
        tagName: "mj-image",
        attributes: compact({
          src: imageSrc2,
          alt: item.title,
          href: item.href,
          // width only — fitted image already has the aspect baked in.
          // Pinning height would re-introduce the stretched-at-other-widths bug.
          width: px(colWidthPx),
          padding: "0px"
        })
      });
    }
  }
  if (include.has("title")) {
    const ts = template.titleStyle ?? {};
    const titleText = isBlank ? "Product title" : item.title;
    nodes.push({
      tagName: "mj-text",
      attributes: compact({
        "font-size": typeof ts.fontSize === "number" ? px(ts.fontSize) : "16px",
        "font-weight": String(ts.fontWeight ?? 700),
        color: typeof ts.color === "string" ? ts.color : theme.colors["text.body"] ?? "#111827",
        align: textAlign,
        padding: "12px 12px 4px"
      }),
      content: escapeHtml(titleText)
    });
  }
  if (include.has("price") && (item.price || isBlank)) {
    const ps = template.priceStyle ?? {};
    const priceText = isBlank ? "$0.00" : item.price ?? "";
    nodes.push({
      tagName: "mj-text",
      attributes: compact({
        "font-size": typeof ps.fontSize === "number" ? px(ps.fontSize) : "14px",
        "font-weight": String(ps.fontWeight ?? 600),
        color: typeof ps.color === "string" ? ps.color : theme.colors["text.body"] ?? "#111827",
        align: textAlign,
        padding: "0px 12px 2px"
      }),
      content: escapeHtml(priceText)
    });
  }
  if (include.has("compareAtPrice") && item.compareAtPrice) {
    nodes.push({
      tagName: "mj-text",
      attributes: compact({
        "font-size": "12px",
        color: theme.colors["text.muted"] ?? "#6B7280",
        align: textAlign,
        padding: "0px 12px 8px"
      }),
      content: `<span style="text-decoration:line-through;">${escapeHtml(item.compareAtPrice)}</span>`
    });
  }
  if (include.has("description") && item.description) {
    nodes.push({
      tagName: "mj-text",
      attributes: compact({
        "font-size": "13px",
        color: theme.colors["text.muted"] ?? "#4B5563",
        align: textAlign,
        padding: "0px 12px 8px"
      }),
      content: escapeHtml(item.description)
    });
  }
  if (include.has("cta")) {
    const variant = template.ctaVariant ?? "primary";
    const btnSpec = theme.buttons[variant];
    nodes.push({
      tagName: "mj-button",
      attributes: compact({
        href: item.href,
        "background-color": btnSpec.backgroundColor,
        color: btnSpec.color,
        "border-radius": px(btnSpec.borderRadius),
        "font-size": px(btnSpec.fontSize),
        "font-weight": String(btnSpec.fontWeight),
        "inner-padding": spacing(btnSpec.padding),
        align: buttonAlign
      }),
      content: escapeHtml(template.ctaLabel ?? "Shop Now")
    });
  }
  return nodes;
}

// src/email-builder/compiler/section-frames.ts
var NON_FLOW_TYPES = /* @__PURE__ */ new Set([
  "PATH",
  "LINE",
  "STAR",
  "POLYGON",
  "TRAPEZOID",
  "SEAL"
]);
function getSectionFrames(doc) {
  const roots = doc.rootChildren.map((id) => doc.nodes[id]).filter((n) => n != null);
  if (roots.length === 1 && roots[0].type === "FRAME" && roots[0].children?.length) {
    return roots[0].children.map((id) => doc.nodes[id]).filter((n) => n != null);
  }
  return roots;
}
function resolveSectionCompileMode(frame, doc) {
  const mode = frame.sectionCompileMode ?? "image";
  if (mode === "live" && !isLiveEligible(frame, doc)) return "image";
  return mode;
}
function getSectionHtmlRef(frame) {
  const raw = frame.pluginData?.nexmailSection;
  if (raw == null || typeof raw !== "object") return null;
  const ref = raw;
  const kind = ref.kind;
  if (kind !== "header" && kind !== "footer") return null;
  return {
    sectionId: typeof ref.sectionId === "string" ? ref.sectionId : void 0,
    sectionName: typeof ref.sectionName === "string" ? ref.sectionName : void 0,
    kind,
    sourceFormat: typeof ref.sourceFormat === "string" ? ref.sourceFormat : void 0,
    html: typeof ref.html === "string" ? ref.html : void 0,
    snapshotUrl: typeof ref.snapshotUrl === "string" ? ref.snapshotUrl : void 0,
    snapshotHash: typeof ref.snapshotHash === "string" ? ref.snapshotHash : void 0,
    insertedAt: typeof ref.insertedAt === "string" ? ref.insertedAt : void 0
  };
}
function getLiveIneligibilityReasons(frame, doc) {
  const reasons = [];
  if (frame.type !== "FRAME" && frame.type !== "GROUP" || (frame.layoutMode ?? "NONE") === "NONE") {
    reasons.push({ code: "not-autolayout", nodeId: frame.id, nodeName: frame.name });
  }
  if (frame.layoutMode === "GRID") {
    reasons.push({ code: "grid", nodeId: frame.id, nodeName: frame.name });
  }
  walkSubtree(frame, doc, (n) => {
    if (NON_FLOW_TYPES.has(n.type)) {
      reasons.push({ code: "vector", nodeId: n.id, nodeName: n.name });
    }
    if (n.id !== frame.id && n.layoutMode === "GRID") {
      reasons.push({ code: "grid", nodeId: n.id, nodeName: n.name });
    }
    if (typeof n.rotation === "number" && Math.abs(n.rotation) > 0.01) {
      reasons.push({ code: "rotated", nodeId: n.id, nodeName: n.name });
    }
    if (n.id !== frame.id && n.layoutPositioning === "ABSOLUTE") {
      reasons.push({ code: "absolute", nodeId: n.id, nodeName: n.name });
    }
  });
  return reasons;
}
function isLiveEligible(frame, doc) {
  return getLiveIneligibilityReasons(frame, doc).length === 0;
}
function describeLiveIneligibilityReason(r) {
  const label = r.nodeName ? `"${r.nodeName}"` : "a layer";
  switch (r.code) {
    case "not-autolayout":
      return "Section is not an auto-layout frame";
    case "grid":
      return "Grid layout has no email-safe equivalent \u2014 sends as an image";
    case "vector":
      return `Contains vector shape ${label}`;
    case "rotated":
      return `${label.charAt(0).toUpperCase() + label.slice(1)} is rotated`;
    case "absolute":
      return `${label.charAt(0).toUpperCase() + label.slice(1)} floats over the layout flow (absolute position)`;
  }
}
function isTextHeavySection(frame, doc) {
  let textNodes = 0;
  let textChars = 0;
  let largeImages = 0;
  walkSubtree(frame, doc, (n) => {
    if (n.visible === false) return;
    if (n.type === "TEXT" && n.text?.trim()) {
      textNodes += 1;
      textChars += n.text.trim().length;
    }
    const isImage = n.type === "IMAGE" || (n.fills ?? []).some((f) => f.type === "IMAGE" && f.visible !== false);
    if (isImage && (n.width > 80 || n.height > 80)) largeImages += 1;
  });
  if (largeImages > 0) return false;
  return textNodes >= 2 || textChars >= 120;
}
function getSectionSendSummary(frame, doc) {
  const requestedMode = frame.sectionCompileMode ?? "image";
  const reasons = requestedMode === "html" ? [] : getLiveIneligibilityReasons(frame, doc);
  const liveEligible = reasons.length === 0 && requestedMode !== "html";
  const effectiveMode = resolveSectionCompileMode(frame, doc);
  let linkZoneCount = 0;
  walkSubtree(frame, doc, (n) => {
    if (n.type === "LINK_ZONE" || n.id !== frame.id && n.linkUrl) linkZoneCount += 1;
  });
  const suggestionDismissed = frame.pluginData?.liveSuggestionDismissed === true;
  return {
    requestedMode,
    effectiveMode,
    liveEligible,
    reasons,
    suggestLive: liveEligible && effectiveMode === "image" && !suggestionDismissed && isTextHeavySection(frame, doc),
    linkZoneCount
  };
}
function extractFrameAsDocument(doc, frameId, maxBottom) {
  const frame = doc.nodes[frameId];
  if (!frame) return null;
  const nodes = {};
  const collect = (id, parentId) => {
    const node = doc.nodes[id];
    if (!node) return;
    nodes[id] = { ...node, parentId };
    for (const childId of node.children ?? []) collect(childId, id);
  };
  collect(frameId, null);
  nodes[frameId] = { ...nodes[frameId], x: 0, y: 0 };
  const frameTop = frame.y ?? 0;
  const frameBottom = frameTop + frame.height;
  const cappedBottom = maxBottom != null && maxBottom > frameTop ? Math.min(frameBottom, maxBottom) : frameBottom;
  return {
    version: 2,
    width: Math.max(1, Math.round(frame.width)),
    height: Math.max(1, Math.round(cappedBottom - frameTop)),
    nodes,
    rootChildren: [frameId],
    fills: frame.fills,
    // Carry the email-level link-zone policy onto the lifted band so the
    // slicer's auto-derive opt-out (`autoLinkZones === false`) survives the
    // per-band extraction.
    autoLinkZones: doc.autoLinkZones
  };
}
function extractBandRunAsDocument(doc, frameIds, maxBottom) {
  const frames = frameIds.map((id) => doc.nodes[id]).filter((n) => !!n);
  if (!frames.length) return null;
  const runTop = Math.min(...frames.map((f) => f.y ?? 0));
  const trueBottom = Math.max(...frames.map((f) => (f.y ?? 0) + (f.height ?? 0)));
  const runBottom = maxBottom != null && maxBottom > runTop ? Math.min(trueBottom, maxBottom) : trueBottom;
  const nodes = {};
  const collect = (id, parentId) => {
    const node = doc.nodes[id];
    if (!node) return;
    nodes[id] = { ...node, parentId };
    for (const childId of node.children ?? []) collect(childId, id);
  };
  for (const f of frames) collect(f.id, null);
  for (const f of frames) {
    nodes[f.id] = { ...nodes[f.id], y: (f.y ?? 0) - runTop };
  }
  return {
    version: 2,
    width: Math.max(1, Math.round(doc.width ?? Math.max(...frames.map((f) => (f.x ?? 0) + (f.width ?? 0))))),
    height: Math.max(1, Math.round(runBottom - runTop)),
    nodes,
    rootChildren: [...frameIds],
    // Preserve the email-level link-zone policy across the run extraction.
    autoLinkZones: doc.autoLinkZones
  };
}
function walkSubtree(node, doc, visit) {
  visit(node);
  for (const childId of node.children ?? []) {
    const child = doc.nodes[childId];
    if (child) walkSubtree(child, doc, visit);
  }
}

// src/email-builder/compiler/renderers/autolayout-to-mjml.ts
function compileLiveFrame(frame, doc) {
  const baseAttrs = sectionBaseAttrs(frame);
  const childNodes = (frame.children ?? []).map((id) => doc.nodes[id]).filter((n) => n != null && n.visible !== false);
  if (frame.layoutMode === "HORIZONTAL" && childNodes.length > 1) {
    return [
      {
        tagName: "mj-section",
        attributes: compact({ ...baseAttrs, padding: framePadding(frame) }),
        children: columnsFromRow(frame, childNodes, doc)
      }
    ];
  }
  const runs = [];
  for (const child of childNodes) {
    if (isColumnRow(child, doc)) {
      runs.push({ kind: "columns", row: child });
    } else {
      const last = runs[runs.length - 1];
      if (last?.kind === "blocks") last.nodes.push(child);
      else runs.push({ kind: "blocks", nodes: [child] });
    }
  }
  const padT = Math.round(frame.layoutPaddingTop ?? 0);
  const padR = Math.round(frame.layoutPaddingRight ?? 0);
  const padB = Math.round(frame.layoutPaddingBottom ?? 0);
  const padL = Math.round(frame.layoutPaddingLeft ?? 0);
  const gap = Math.max(0, Math.round(frame.layoutGap ?? 0));
  const bandAlign = frame.layoutMode === "HORIZONTAL" ? axisAlignToTextOpt(frame.primaryAxisAlign) : counterAxisToTextOpt(frame.counterAxisAlign);
  return runs.map((run, i) => {
    const first = i === 0;
    const last = i === runs.length - 1;
    const padding = `${first ? padT : 0}px ${padR}px ${last ? padB : gap}px ${padL}px`;
    const attributes = compact({ ...baseAttrs, padding });
    if (run.kind === "columns") {
      const kids = (run.row.children ?? []).map((id) => doc.nodes[id]).filter((n) => n != null && n.visible !== false);
      return {
        tagName: "mj-section",
        attributes,
        children: columnsFromRow(run.row, kids, doc)
      };
    }
    return {
      tagName: "mj-section",
      attributes,
      children: [
        {
          tagName: "mj-column",
          attributes: compact({ "css-class": "nm-col" }),
          children: run.nodes.flatMap(
            (child, j) => renderBlock(child, doc, j < run.nodes.length - 1 ? frame.layoutGap : 0, bandAlign)
          )
        }
      ]
    };
  });
}
function sectionBaseAttrs(frame) {
  const imageFill = frame.fills?.find((f) => f.visible !== false && f.type === "IMAGE");
  return {
    // Provenance for the Klaviyo editable-export transformer: a live band is an
    // editable section. All runs of one band share these attrs, so each emitted
    // mj-section inherits the marker. See klaviyo-html-transformer.service.ts.
    "css-class": joinClasses("nm-sec", `nm-id-${frame.id}`),
    "background-color": solidFill(frame.fills),
    ...imageFill && imageFill.type === "IMAGE" && imageFill.src ? {
      "background-url": imageFill.src,
      "background-size": "cover",
      "background-position": "center center"
    } : {}
  };
}
function isColumnRow(node, doc) {
  if (node.type !== "FRAME" && node.type !== "GROUP") return false;
  if (node.layoutMode !== "HORIZONTAL") return false;
  if (isButtonLike(node, doc)) return false;
  const kids = (node.children ?? []).map((id) => doc.nodes[id]).filter((n) => n != null && n.visible !== false);
  if (kids.length < 2) return false;
  const inline = kids.every((k) => k.type === "TEXT") || kids.every((k) => k.type === "IMAGE");
  return !inline;
}
function columnsFromRow(row, kids, doc) {
  const total = kids.reduce((sum, c) => sum + Math.max(1, c.width), 0);
  const valign = counterAxisToValign(row.counterAxisAlign);
  return kids.map((child) => ({
    tagName: "mj-column",
    attributes: compact({
      "css-class": joinClasses("nm-col", `nm-id-${child.id}`),
      width: `${Math.round(Math.max(1, child.width) / total * 100)}%`,
      "vertical-align": valign
    }),
    children: renderBlock(child, doc)
  }));
}
function renderBlock(node, doc, gapBelow = 0, fallbackAlign) {
  if (node.visible === false) return [];
  switch (node.type) {
    case "TEXT":
      return [mjText(node, gapBelow)];
    case "IMAGE":
      return [mjImage(node, gapBelow)];
    case "RECTANGLE": {
      if (node.height <= 6 && solidFill(node.fills)) {
        return [mjDivider(node, gapBelow)];
      }
      return [];
    }
    case "FRAME":
    case "GROUP": {
      if (isButtonLike(node, doc)) return [mjButton(node, doc, gapBelow)];
      const kids = (node.children ?? []).map((id) => doc.nodes[id]).filter((n) => n != null && n.visible !== false);
      if (kids.length === 0) {
        const h = Math.round(node.height);
        return h > 0 ? [{ tagName: "mj-spacer", attributes: compact({ height: px(h) }) }] : [];
      }
      if (node.layoutMode === "HORIZONTAL") {
        const row = renderInlineRow(node, doc, gapBelow, fallbackAlign);
        if (row) return [row];
      }
      const childAlign = counterAxisToTextOpt(node.counterAxisAlign) ?? fallbackAlign;
      return kids.flatMap(
        (child, i) => renderBlock(child, doc, i < kids.length - 1 ? node.layoutGap : gapBelow, childAlign)
      );
    }
    default:
      return [];
  }
}
function mjText(node, gapBelow) {
  const color = solidFill(node.fills) ?? "#000000";
  const inner = textInnerHtml(node.text ?? "", node.linkUrl, color);
  return {
    tagName: "mj-text",
    attributes: compact({
      "css-class": joinClasses("nm-blk", "nm-blk-text", `nm-id-${node.id}`),
      align: node.textAlign ?? "left",
      color,
      "font-family": fontFamilyCss(node.fontFamily),
      "font-size": px(node.fontSize ?? 14),
      "font-weight": String(node.fontWeight ?? 400),
      "line-height": lineHeight(node.lineHeight ?? 1.4),
      "letter-spacing": node.letterSpacing ? px(node.letterSpacing) : void 0,
      "text-transform": node.textTransform && node.textTransform !== "none" ? node.textTransform : void 0,
      padding: blockPadding(gapBelow)
    }),
    content: inner
  };
}
function mjImage(node, gapBelow) {
  return {
    tagName: "mj-image",
    attributes: compact({
      "css-class": joinClasses("nm-blk", "nm-blk-image", `nm-id-${node.id}`),
      src: node.imageSrc ?? "",
      alt: node.imageAlt ?? "",
      href: node.linkUrl,
      width: px(Math.round(node.width)),
      align: "center",
      padding: blockPadding(gapBelow)
    })
  };
}
function mjDivider(node, gapBelow) {
  return {
    tagName: "mj-divider",
    attributes: compact({
      "css-class": joinClasses("nm-blk", "nm-blk-divider", `nm-id-${node.id}`),
      "border-width": px(Math.max(1, Math.round(node.height))),
      "border-color": solidFill(node.fills),
      padding: blockPadding(gapBelow)
    })
  };
}
function mjButton(node, doc, gapBelow) {
  const label = firstTextChild(node, doc);
  const bg = solidFill(node.fills) ?? "#000000";
  const color = label ? solidFill(label.fills) ?? "#FFFFFF" : "#FFFFFF";
  return {
    tagName: "mj-button",
    attributes: compact({
      "css-class": joinClasses("nm-blk", "nm-blk-button", `nm-id-${node.id}`),
      href: node.linkUrl ?? label?.linkUrl ?? "#",
      "background-color": bg,
      color,
      "font-family": fontFamilyCss(label?.fontFamily),
      "font-size": px(label?.fontSize ?? 14),
      "font-weight": String(label?.fontWeight ?? 600),
      "border-radius": typeof node.cornerRadius === "number" ? px(node.cornerRadius) : void 0,
      padding: blockPadding(gapBelow)
    }),
    content: escapeHtml(label?.text ?? "")
  };
}
function renderInlineRow(node, doc, gapBelow, fallbackAlign) {
  const kids = (node.children ?? []).map((id) => doc.nodes[id]).filter((n) => n != null && n.visible !== false);
  if (!kids.length) return null;
  const align = axisAlignToTextOpt(node.primaryAxisAlign) ?? fallbackAlign ?? "left";
  const gapPx = Math.max(0, Math.round(node.layoutGap ?? 8));
  const sep = `<span style="display:inline-block;width:${gapPx}px;"></span>`;
  if (kids.every((k) => k.type === "TEXT")) {
    const color = solidFill(kids[0].fills) ?? "#000000";
    const content = kids.map((k) => textInnerHtml(k.text ?? "", k.linkUrl, solidFill(k.fills) ?? color)).join(sep);
    return {
      tagName: "mj-text",
      attributes: compact({
        "css-class": joinClasses("nm-blk", "nm-blk-text"),
        align,
        color,
        "font-family": fontFamilyCss(kids[0].fontFamily),
        "font-size": px(kids[0].fontSize ?? 14),
        "font-weight": String(kids[0].fontWeight ?? 400),
        padding: blockPadding(gapBelow)
      }),
      content
    };
  }
  if (kids.every((k) => k.type === "IMAGE")) {
    const content = kids.map((k) => {
      const img = `<img src="${escapeHtml(k.imageSrc ?? "")}" alt="${escapeHtml(k.imageAlt ?? "")}" height="${Math.round(k.height)}" style="height:${Math.round(k.height)}px;border:0;vertical-align:middle;" />`;
      return k.linkUrl ? `<a href="${escapeHtml(k.linkUrl)}" style="text-decoration:none;">${img}</a>` : img;
    }).join(sep);
    return {
      tagName: "mj-text",
      // Inline image rows (social-icon strips) ship as one mj-text of inline
      // <img>s, so the Klaviyo block type is text (a text/HTML block), not a
      // single swappable image.
      attributes: compact({ "css-class": joinClasses("nm-blk", "nm-blk-text"), align, padding: blockPadding(gapBelow) }),
      content
    };
  }
  return null;
}
function textInnerHtml(text, linkUrl, color) {
  const html = escapeHtml(text).replace(/\n/g, "<br/>");
  if (!linkUrl) return html;
  return `<a href="${escapeHtml(linkUrl)}" style="color:${color};text-decoration:underline;">${html}</a>`;
}
function fontFamilyCss(family) {
  return family ? `'${family}', 'Roboto', sans-serif` : `'Roboto', sans-serif`;
}
function isButtonLike(node, doc) {
  const href = node.linkUrl;
  const hasFill = !!solidFill(node.fills);
  const kids = (node.children ?? []).map((id) => doc.nodes[id]).filter(Boolean);
  const onlyText = kids.length === 1 && kids[0].type === "TEXT";
  return !!href && hasFill && onlyText;
}
function firstTextChild(node, doc) {
  for (const id of node.children ?? []) {
    const child = doc.nodes[id];
    if (child?.type === "TEXT") return child;
  }
  return void 0;
}
function axisAlignToTextOpt(align) {
  if (align === "CENTER" || align === "SPACE_BETWEEN") return "center";
  if (align === "END") return "right";
  if (align === "START") return "left";
  return void 0;
}
function counterAxisToTextOpt(align) {
  if (align === "CENTER") return "center";
  if (align === "END") return "right";
  if (align === "START") return "left";
  return void 0;
}
function counterAxisToValign(align) {
  if (align === "CENTER") return "middle";
  if (align === "END") return "bottom";
  if (align === "START") return "top";
  return void 0;
}
function framePadding(frame) {
  const t = Math.round(frame.layoutPaddingTop ?? 0);
  const r = Math.round(frame.layoutPaddingRight ?? 0);
  const b = Math.round(frame.layoutPaddingBottom ?? 0);
  const l = Math.round(frame.layoutPaddingLeft ?? 0);
  if (!t && !r && !b && !l) return void 0;
  return `${t}px ${r}px ${b}px ${l}px`;
}
function blockPadding(gapBelow) {
  const g = Math.round(gapBelow);
  return g > 0 ? `0px 0px ${g}px 0px` : "0px";
}
function solidFill(fills) {
  const fill = fills?.find((f) => f.visible !== false && f.type === "SOLID");
  if (!fill || fill.type !== "SOLID") return void 0;
  if (fill.opacity != null && fill.opacity < 1) {
    const m = fill.color.trim().match(/^#([0-9a-fA-F]{6})$/);
    if (m) {
      const r = parseInt(m[1].slice(0, 2), 16);
      const g = parseInt(m[1].slice(2, 4), 16);
      const b = parseInt(m[1].slice(4, 6), 16);
      return `rgba(${r},${g},${b},${Number(fill.opacity.toFixed(3))})`;
    }
  }
  return fill.color;
}

// src/email-builder/compiler/renderers/canvas-section.ts
async function compileCanvasSectionNode(node, ctx) {
  const scene = node.props.canvasJson;
  if (!scene) return [await compileCanvasSection(node, ctx)];
  const frames = getSectionFrames(scene);
  for (const frame of frames) {
    const requested = frame.sectionCompileMode ?? "image";
    if (requested === "html") continue;
    const effective = resolveSectionCompileMode(frame, scene);
    if (requested !== effective) {
      ctx.reportSectionWarning?.({
        frameId: frame.id,
        frameName: frame.name,
        requestedMode: requested,
        effectiveMode: effective,
        reasons: getLiveIneligibilityReasons(frame, scene).map(
          describeLiveIneligibilityReason
        )
      });
    }
  }
  const needsBandSplit = frames.some((f) => {
    const mode = resolveSectionCompileMode(f, scene);
    return mode === "live" || mode === "html";
  });
  if (!needsBandSplit) return [await compileCanvasSection(node, ctx)];
  const brand = brandContextFromCompileCtx(ctx);
  const resolvedNode = brand ? resolveCanvasMergeTags(node, brand) : JSON.parse(JSON.stringify(node));
  const resolvedScene = resolvedNode.props.canvasJson;
  const out = [];
  let run = [];
  const flushRun = async (clampBottom) => {
    if (run.length === 0) return;
    if (run.length === 1) {
      const frame = run[0];
      const rawBand = extractFrameAsDocument(scene, frame.id, clampBottom);
      const bandSceneHash = rawBand ? ctx.hash(JSON.stringify(rawBand)) : void 0;
      out.push(
        await renderBandAsImage(
          frame,
          resolvedScene,
          resolveSectionCompileMode(frame, resolvedScene),
          ctx,
          bandSceneHash,
          clampBottom
        )
      );
    } else {
      out.push(await renderBandRunAsImage(run, resolvedScene, ctx, clampBottom));
    }
    run = [];
  };
  for (const frame of getSectionFrames(resolvedScene)) {
    const mode = resolveSectionCompileMode(frame, resolvedScene);
    if (mode === "html") {
      await flushRun(frame.y);
      out.push(await compileHtmlPassthroughBand(frame, ctx));
    } else if (mode === "live") {
      await flushRun(frame.y);
      await resolveLiveFrameImages(frame, resolvedScene, ctx);
      out.push(...compileLiveFrame(frame, resolvedScene));
    } else {
      run.push(frame);
    }
  }
  await flushRun();
  return out;
}
async function resolveLiveFrameImages(frame, scene, ctx) {
  const definitelyOpaque = (src) => /\.jpe?g($|[?#])/i.test(src);
  const visit = async (node) => {
    const width = Math.max(1, Math.round(node.width));
    if (node.type === "IMAGE" && node.imageSrc) {
      node.imageSrc = await ctx.resolveImage(node.imageSrc, {
        width,
        transparent: !definitelyOpaque(node.imageSrc) || void 0
      });
    }
    for (const fill of node.fills ?? []) {
      if (fill.type === "IMAGE" && fill.src) {
        fill.src = await ctx.resolveImage(fill.src, {
          width,
          transparent: !definitelyOpaque(fill.src) || void 0
        });
      }
    }
    for (const id of node.children ?? []) {
      const child = scene.nodes[id];
      if (child) await visit(child);
    }
  };
  await visit(frame);
}
var MINIMAL_COMPLIANCE_FOOTER_HTML = '<div style="padding:24px 16px;text-align:center;font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#666666;"><a href="[NM:UNSUBSCRIBE_URL]" style="color:#666666;text-decoration:underline;">Unsubscribe</a></div>';
function bandRoleClass(frame) {
  const ref = getSectionHtmlRef(frame);
  return ref ? SECTION_ROLE_CLASSES[ref.kind] : void 0;
}
async function compileHtmlPassthroughBand(frame, ctx) {
  const roleClass = bandRoleClass(frame);
  const wrapRaw = (html) => ({
    tagName: "mj-section",
    // nm-sec-static marks a rasterized / passthrough band so the Klaviyo
    // editable-export transformer leaves it as a static block (you can't
    // drag-edit a baked image slice or a header/footer HTML passthrough).
    attributes: {
      padding: "0",
      "css-class": joinClasses("nm-sec", "nm-sec-static", roleClass)
    },
    children: [
      {
        tagName: "mj-column",
        attributes: { padding: "0" },
        // mj-column emits mj-raw straight into its <tbody>; anything but a
        // row is foster-parented out of the table and its layout torn apart.
        children: [{ tagName: "mj-raw", content: `<tr><td style="padding:0;">${html}</td></tr>` }]
      }
    ]
  });
  const ref = getSectionHtmlRef(frame);
  if (ref && ctx.resolveSectionHtml) {
    try {
      const html = await ctx.resolveSectionHtml({
        sectionId: ref.sectionId,
        kind: ref.kind
      });
      if (html) return wrapRaw(html);
    } catch (error) {
      ctx.logger.warn(
        `html band ${frame.id}: resolveSectionHtml failed, using baked HTML (${error instanceof Error ? error.message : String(error)})`
      );
    }
  }
  if (ref?.html) return wrapRaw(ref.html);
  ctx.reportSectionWarning?.({
    frameId: frame.id,
    frameName: frame.name,
    requestedMode: "html",
    effectiveMode: "html",
    reasons: [
      "Section HTML could not be resolved; a minimal unsubscribe footer was emitted instead"
    ]
  });
  return wrapRaw(MINIMAL_COMPLIANCE_FOOTER_HTML);
}
async function renderBandAsImage(frame, scene, _mode, ctx, bandSceneHash, maxBottom) {
  const sub = extractFrameAsDocument(scene, frame.id, maxBottom);
  const roleClass = bandRoleClass(frame);
  const wrapRaw = (html2) => ({
    tagName: "mj-section",
    // nm-sec-static marks a rasterized / passthrough band so the Klaviyo
    // editable-export transformer leaves it as a static block (you can't
    // drag-edit a baked image slice or a header/footer HTML passthrough).
    attributes: {
      padding: "0",
      "css-class": joinClasses("nm-sec", "nm-sec-static", roleClass)
    },
    children: [
      {
        tagName: "mj-column",
        attributes: { padding: "0" },
        children: [{ tagName: "mj-raw", content: html2 }]
      }
    ]
  });
  if (!sub) return wrapRaw("");
  const synthetic = {
    id: frame.id,
    type: "canvas-section",
    props: {
      alt: frame.name ?? "",
      canvasJson: sub,
      scene: { width: sub.width, height: sub.height },
      // Carried for the host's clickMap-aware slice: `id` is the bandId and
      // `bandSceneHash` is the freshness key. The host (which can read the
      // template's clickMap; this pure compiler can't) matches them to decide
      // whether to slice the stored backdrop or fall back to a live slice.
      ...bandSceneHash ? { bandSceneHash } : {}
    },
    children: []
  };
  const rendered = await ctx.renderCanvas(synthetic, ctx.theme, { slice: true });
  const html = rendered.html ?? imgFallbackHtml(rendered.url, rendered.width, frame.name);
  return wrapRaw(html);
}
async function renderBandRunAsImage(frames, scene, ctx, maxBottom) {
  const wrapRaw = (html2) => ({
    tagName: "mj-section",
    // nm-sec-static marks a rasterized / passthrough band so the Klaviyo
    // editable-export transformer leaves it as a static block (you can't
    // drag-edit a baked image slice or a header/footer HTML passthrough).
    attributes: { padding: "0", "css-class": "nm-sec nm-sec-static" },
    children: [
      {
        tagName: "mj-column",
        attributes: { padding: "0" },
        children: [{ tagName: "mj-raw", content: html2 }]
      }
    ]
  });
  const sub = extractBandRunAsDocument(scene, frames.map((f) => f.id), maxBottom);
  if (!sub) return wrapRaw("");
  const synthetic = {
    id: frames[0].id,
    type: "canvas-section",
    props: {
      alt: frames[0].name ?? "",
      canvasJson: sub,
      scene: { width: sub.width, height: sub.height }
    },
    children: []
  };
  const rendered = await ctx.renderCanvas(synthetic, ctx.theme, { slice: true });
  const html = rendered.html ?? imgFallbackHtml(rendered.url, rendered.width, frames[0].name);
  return wrapRaw(html);
}
async function compileCanvasSection(node, ctx) {
  const wrap = (html) => ({
    tagName: "mj-section",
    // nm-sec-static marks a rasterized / passthrough band so the Klaviyo
    // editable-export transformer leaves it as a static block (you can't
    // drag-edit a baked image slice or a header/footer HTML passthrough).
    attributes: { padding: "0", "css-class": "nm-sec nm-sec-static" },
    children: [
      {
        tagName: "mj-column",
        attributes: { padding: "0" },
        children: [{ tagName: "mj-raw", content: html }]
      }
    ]
  });
  try {
    const brand = brandContextFromCompileCtx(ctx);
    const resolvedNode = brand ? resolveCanvasMergeTags(node, brand) : node;
    const hashSource = resolvedNode.props.canvasJson ?? resolvedNode.children;
    const hash = ctx.hash(JSON.stringify(hashSource));
    if (node.cached?.hash === hash && node.cached.html) {
      return wrap(node.cached.html);
    }
    const rendered = await ctx.renderCanvas(resolvedNode, ctx.theme, { slice: true });
    const html = rendered.html ?? imgFallbackHtml(rendered.url, rendered.width, node.props.alt);
    node.cached = {
      url: rendered.url,
      html,
      width: rendered.width,
      height: rendered.height,
      // Store the same resolved-tree sha256 the hit-check above compares
      // against. rendered.hash is the host's cheap bookkeeping hash and never
      // equals it -- storing that made the cache miss on every compile.
      hash,
      renderedAt: ctx.now()
    };
    return wrap(html);
  } catch (error) {
    if (node.props.fallbackText) {
      return {
        tagName: "mj-section",
        children: [
          {
            tagName: "mj-column",
            children: [{ tagName: "mj-text", content: escapeHtml(node.props.fallbackText) }]
          }
        ]
      };
    }
    throw {
      kind: "resolution",
      source: "canvas",
      retryable: true,
      cause: error instanceof Error ? error.message : "Canvas render failed",
      nodeId: node.id
    };
  }
}
function brandContextFromCompileCtx(ctx) {
  const brand = ctx.brand;
  const theme = ctx.theme;
  if (!brand && !theme) return void 0;
  const primaryRadius = theme?.buttons?.primary?.borderRadius;
  return {
    name: brand?.name,
    logoUrl: brand?.assets?.logo,
    borderRadius: typeof primaryRadius === "number" ? `${primaryRadius}px` : void 0,
    colorByUsage: theme?.colors,
    fontByUsage: theme?.typography?.families,
    // Strip undefined values so the resolver never returns the literal "undefined"
    // for an empty platform — keeps unset $brand.social.X tags as-is so the editor
    // can flag them.
    socialByPlatform: brand?.socialLinks ? Object.fromEntries(
      Object.entries(brand.socialLinks).filter(([, v]) => typeof v === "string" && v.length > 0)
    ) : void 0
  };
}
function resolveCanvasMergeTags(node, brand) {
  const cloned = JSON.parse(JSON.stringify(node));
  resolveMergeTagsInTree(cloned, brand, void 0);
  coerceNumericSceneFields(cloned);
  return cloned;
}
var NUMERIC_SCENE_FIELDS = ["cornerRadius"];
function coerceNumericSceneFields(subtree) {
  if (subtree == null || typeof subtree !== "object") return;
  if (Array.isArray(subtree)) {
    for (const item of subtree) coerceNumericSceneFields(item);
    return;
  }
  const obj = subtree;
  for (const field of NUMERIC_SCENE_FIELDS) {
    const v = obj[field];
    if (typeof v === "string") {
      const match = v.match(/^(-?\d+(?:\.\d+)?)(?:px|em|rem|%)?$/);
      if (match) obj[field] = parseFloat(match[1]);
    }
  }
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v && typeof v === "object") coerceNumericSceneFields(v);
  }
}
async function compileRootCanvasObject(node, ctx) {
  const wrap = (imgAttrs) => ({
    tagName: "mj-section",
    children: [
      {
        tagName: "mj-column",
        children: [{ tagName: "mj-image", attributes: imgAttrs }]
      }
    ]
  });
  const { width, height } = node.props.transform;
  const syntheticNode = {
    id: node.id,
    type: "canvas-section",
    props: {
      alt: node.props.alt ?? "",
      scene: { width, height }
    },
    children: [node]
  };
  const alt = node.props.alt ?? "";
  try {
    const hashSource = node.props;
    const hash = ctx.hash(JSON.stringify(hashSource));
    if (node.cached?.hash === hash) {
      const cached = node.cached;
      return wrap(compact({ src: cached.url, alt, width: px(cached.width) }));
    }
    const rendered = await ctx.renderCanvas(syntheticNode, ctx.theme);
    node.cached = {
      url: rendered.url,
      width: rendered.width,
      height: rendered.height,
      // Same key the hit-check above computes; rendered.hash is the host's
      // bookkeeping hash and never matches it.
      hash,
      renderedAt: ctx.now()
    };
    return wrap(compact({ src: rendered.url, alt, width: px(rendered.width) }));
  } catch (error) {
    throw {
      kind: "resolution",
      source: "canvas",
      retryable: true,
      cause: error instanceof Error ? error.message : "Canvas render failed",
      nodeId: node.id
    };
  }
}
function imgFallbackHtml(url, width, alt) {
  const altAttr = alt ? ` alt="${escapeHtml(alt)}"` : ' alt=""';
  return `<img src="${escapeHtml(url)}" width="${Math.round(width)}"${altAttr} style="display:block;width:100%;max-width:${Math.round(width)}px;border:0;" />`;
}

// src/email-builder/compiler/canvas-first.ts
function isCanvasFirstEmail(doc) {
  const body = doc.root.children.filter((c) => c.type !== "head");
  let canvas = null;
  for (const child of body) {
    if (child.type === "canvas-section") {
      if (canvas) return null;
      canvas = child;
      continue;
    }
    if (child.type !== "section" && child.type !== "wrapper") return null;
  }
  return canvas;
}

// src/email-builder/compiler/compile-email-document.ts
function mobileColumnPaddingClass(spacingCss) {
  return `nm-m-cpad-${spacingCss.replace(/px/g, "").trim().replace(/\s+/g, "-")}`;
}
async function compileEmailDocument(document, ctx) {
  const brandCtx = brandContextFromCompileCtx(ctx);
  if (brandCtx) {
    resolveMergeTagsInTree(document, brandCtx, void 0);
  }
  const state = {
    minHeightPx: /* @__PURE__ */ new Set(),
    mobileTextAligns: /* @__PURE__ */ new Set(),
    mobileColumnPaddings: /* @__PURE__ */ new Set(),
    hasSocialIcons: false
  };
  const head = document.root.children[0];
  const bodyNodes = (await Promise.all(
    document.root.children.slice(1).map((child) => compileBodyNode(child, ctx, document.root.props.width, ctx.brand, state))
  )).flat();
  return {
    tagName: "mjml",
    attributes: {},
    children: [
      compileHead(head, ctx, state),
      {
        tagName: "mj-body",
        attributes: compact({ "background-color": asString(document.root.style?.backgroundColor) }),
        children: bodyNodes
      }
    ]
  };
}
function compileHead(node, ctx, state) {
  const children = [
    { tagName: "mj-title", content: ctx.subjectLine },
    { tagName: "mj-preview", content: ctx.previewText }
  ];
  for (const font of node.props.fonts ?? []) {
    if (font.importUrl) {
      children.push({
        tagName: "mj-style",
        content: `@import url(${font.importUrl});`
      });
    }
  }
  const attributeChildren = [
    {
      tagName: "mj-all",
      attributes: compact({
        "text-align": node.props.defaults?.textAlign,
        padding: spacing(node.props.defaults?.padding) ?? "0"
      })
    },
    { tagName: "mj-text", attributes: { padding: "10px" } },
    { tagName: "mj-button", attributes: { padding: "10px" } },
    { tagName: "mj-divider", attributes: { padding: "10px 0" } },
    { tagName: "mj-social", attributes: { padding: "10px" } },
    // Same trap as mj-navbar-link below: MJML gives mj-social-element a 4px
    // default padding, but `mj-all padding="0"` above outranks it, so every
    // icon td rendered `padding:0` and the icons sat edge to edge. 6px
    // left/right ≈ 12px between icons, matching the editor preview's MUI
    // `spacing={1.5}`.
    { tagName: "mj-social-element", attributes: { padding: "0 6px" } },
    { tagName: "mj-navbar", attributes: { padding: "10px" } },
    // mj-navbar-link needs its own spacing — the editor uses a 20px gap via
    // MUI `spacing={2.5}`. Without this, the `mj-all padding="0"` above
    // collapses all nav-link padding and link labels run together
    // ("SHOPABOUT"). 10px left/right ≈ 20px between links.
    //
    // text-transform: none overrides MJML's default 'uppercase' so the
    // author's casing is preserved. letter-spacing: normal cancels the
    // default 1px tracking that goes with the uppercase look.
    {
      tagName: "mj-navbar-link",
      attributes: {
        padding: "0 10px",
        "text-transform": "none",
        "letter-spacing": "0px"
      }
    },
    // mj-image padding intentionally 0 so images fill their column, matching
    // the editor. Authors who want spacing around an image set `style.padding`
    // on the node explicitly.
    { tagName: "mj-image", attributes: { padding: "0" } }
  ];
  children.push({ tagName: "mj-attributes", children: attributeChildren });
  if (node.props.customAttrs && Object.keys(node.props.customAttrs).length > 0) {
    const cssVars = Object.entries(node.props.customAttrs).map(([key, value]) => `--${key}:${value};`).join(" ");
    children.push({
      tagName: "mj-style",
      attributes: { inline: "inline" },
      content: `:root { ${cssVars} }`
    });
  }
  if (state.minHeightPx.size > 0) {
    const rules = Array.from(state.minHeightPx).map((h) => `.mjh-${h} > table, .mjh-${h} > table > tbody, .mjh-${h} > table > tbody > tr, .mjh-${h} > table > tbody > tr > td { height: 100%; } .mjh-${h} { min-height: ${h}px !important; }`).join("\n");
    children.push({ tagName: "mj-style", content: rules });
  }
  if (state.mobileTextAligns.size > 0) {
    const rules = Array.from(state.mobileTextAligns).map((a) => `.nm-m-align-${a} { text-align: ${a} !important; }`).join(" ");
    children.push({
      tagName: "mj-style",
      content: `@media only screen and (max-width:480px) { ${rules} }`
    });
  }
  if (state.mobileColumnPaddings.size > 0) {
    const rules = Array.from(state.mobileColumnPaddings).map((s) => `.${mobileColumnPaddingClass(s)} > table > tbody > tr > td { padding: ${s} !important; }`).join(" ");
    children.push({
      tagName: "mj-style",
      content: `@media only screen and (max-width:480px) { ${rules} }`
    });
  }
  if (state.hasSocialIcons) {
    children.push({
      tagName: "mj-style",
      content: "@media (prefers-color-scheme: dark) { .nm-social-icon img { filter: invert(1) !important; } }"
    });
  }
  return { tagName: "mj-head", children };
}
async function compileBodyNode(node, ctx, emailWidth, brand, state) {
  switch (node.type) {
    case "wrapper": {
      const children = (await Promise.all(node.children.map((child) => compileBodyNode(child, ctx, emailWidth, brand, state)))).flat();
      return [
        {
          tagName: "mj-wrapper",
          attributes: compact({
            "background-color": asString(node.style?.backgroundColor),
            "background-url": node.style?.backgroundImage,
            padding: spacing(node.style?.padding),
            "border-radius": borderRadius(node.style?.borderRadius),
            "full-width": node.props.fullWidth ? "full-width" : void 0
          }),
          children
        }
      ];
    }
    case "section": {
      const rasterized = ctx.rasterizedNodes?.get(node.id);
      if (rasterized) {
        return [
          {
            tagName: "mj-section",
            attributes: compact({
              // Rasterized section = a baked image, static for Klaviyo export.
              "css-class": joinClasses(
                "nm-sec",
                "nm-sec-static",
                sectionRoleClass(node),
                `nm-id-${node.id}`
              ),
              padding: "0px",
              "full-width": node.props.fullWidth ? "full-width" : void 0
            }),
            children: [
              {
                tagName: "mj-column",
                attributes: { padding: "0px" },
                children: [
                  {
                    tagName: "mj-image",
                    attributes: compact({
                      src: rasterized.url,
                      width: `${rasterized.width}px`,
                      alt: node.props.imageAlt ?? node.style?.backgroundImageAlt ?? "",
                      href: node.props.imageHref ?? node.props.href,
                      padding: "0px"
                    })
                  }
                ]
              }
            ]
          }
        ];
      }
      const padLeft = typeof node.style?.padding === "object" ? node.style.padding.left ?? 0 : 0;
      const padRight = typeof node.style?.padding === "object" ? node.style.padding.right ?? 0 : 0;
      const sectionInnerWidth = Math.max(0, emailWidth - padLeft - padRight);
      const [bg, children] = await Promise.all([
        resolveSectionBackground(node, ctx),
        Promise.all(node.children.map((child) => compileBodyNode(child, ctx, sectionInnerWidth, brand, state))).then((arr) => arr.flat())
      ]);
      return [
        {
          tagName: "mj-section",
          attributes: compact({
            "css-class": joinClasses("nm-sec", sectionRoleClass(node), `nm-id-${node.id}`),
            "background-color": bg.backgroundColor,
            "background-url": bg.backgroundUrl,
            "background-size": bg.backgroundSize,
            "background-position": bg.backgroundPosition,
            "background-repeat": bg.backgroundRepeat,
            padding: spacing(node.style?.padding),
            "border-radius": borderRadius(node.style?.borderRadius),
            ...border(node.style?.border),
            "border-top": borderSide(node.style?.borderTop),
            "border-right": borderSide(node.style?.borderRight),
            "border-bottom": borderSide(node.style?.borderBottom),
            "border-left": borderSide(node.style?.borderLeft),
            "full-width": node.props.fullWidth ? "full-width" : void 0
          }),
          children
        }
      ];
    }
    case "group": {
      const rasterizedGroup = ctx.rasterizedNodes?.get(node.id);
      if (rasterizedGroup) {
        const groupAlt = node.props.imageAlt ?? "";
        const groupHref = node.props.imageHref;
        return [
          {
            tagName: "mj-group",
            attributes: compact({ width: node.props.width }),
            children: [
              {
                tagName: "mj-column",
                attributes: { padding: "0px" },
                children: [
                  {
                    tagName: "mj-image",
                    attributes: compact({
                      src: rasterizedGroup.url,
                      width: `${rasterizedGroup.width}px`,
                      alt: groupAlt,
                      href: groupHref,
                      padding: "0px"
                    })
                  }
                ]
              }
            ]
          }
        ];
      }
      const colWidths = computeColumnWidths(node.children, emailWidth);
      const columns = (await Promise.all(
        node.children.map(
          (child, i) => compileBodyNode(child, ctx, colWidths[i] ?? emailWidth, brand, state)
        )
      )).flat();
      const groupMinHeight = computeGroupMinHeight(node.children);
      if (groupMinHeight > 0) {
        state.minHeightPx.add(groupMinHeight);
        const className = `mjh-${groupMinHeight}`;
        for (const col of columns) {
          if (col.tagName === "mj-column") {
            const existing = col.attributes?.["css-class"];
            col.attributes = {
              ...col.attributes ?? {},
              "css-class": existing ? `${existing} ${className}` : className
            };
          }
        }
      }
      if (node.props.preventStackingOnMobile === false) return columns;
      return [
        {
          tagName: "mj-group",
          attributes: compact({ width: node.props.width }),
          children: columns
        }
      ];
    }
    case "column":
      return [await compileColumn(node, ctx, emailWidth, brand, state)];
    case "product-feed":
      return compileProductFeed(node, ctx, emailWidth);
    case "canvas-section":
      return await compileCanvasSectionNode(node, ctx);
    case "canvas-shape":
    case "canvas-text":
    case "canvas-image":
    case "canvas-path":
    case "canvas-group":
      return [await compileRootCanvasObject(node, ctx)];
    default:
      throw new Error(`Unsupported body node type: ${node.type}`);
  }
}
async function resolveSectionBackground(node, ctx) {
  const style = node.style ?? {};
  const backgroundColor = asString(style.backgroundColor);
  const fallbackColor = backgroundColor ?? gradientFirstStop(style.backgroundGradient);
  let backgroundUrl = style.backgroundImage;
  let backgroundSize = style.backgroundSize;
  let backgroundPosition = style.backgroundPosition;
  let backgroundRepeat;
  if (!backgroundUrl && style.backgroundGradient) {
    const rendered = await ctx.resolveGradientBackground(
      gradientToCss(style.backgroundGradient)
    );
    if (rendered) {
      backgroundUrl = rendered;
      backgroundSize = backgroundSize ?? "cover";
      backgroundPosition = backgroundPosition ?? "center center";
      backgroundRepeat = "no-repeat";
    }
  }
  if (style.backgroundImage && style.backgroundGradient) {
    try {
      backgroundUrl = await ctx.compositeOverlayOntoImage(
        style.backgroundImage,
        gradientToCss(style.backgroundGradient)
      );
    } catch (err) {
      ctx.logger.warn(`gradient overlay failed on node ${node.id}: ${err instanceof Error ? err.message : err}`);
    }
  }
  if (backgroundUrl && style.backgroundOverlay) {
    try {
      backgroundUrl = await ctx.compositeOverlayOntoImage(
        backgroundUrl,
        overlayToCss(style.backgroundOverlay)
      );
    } catch (err) {
      ctx.logger.warn(`solid overlay failed on node ${node.id}: ${err instanceof Error ? err.message : err}`);
    }
  }
  if (backgroundUrl) {
    backgroundRepeat = backgroundRepeat ?? "no-repeat";
    backgroundSize = backgroundSize ?? "cover";
    backgroundPosition = backgroundPosition ?? "center center";
  }
  return {
    backgroundUrl,
    backgroundColor: fallbackColor,
    backgroundSize,
    backgroundPosition,
    backgroundRepeat
  };
}
async function compileColumn(node, ctx, emailWidth, brand, state) {
  const rasterizedColumn = ctx.rasterizedNodes?.get(node.id);
  if (rasterizedColumn) {
    const columnAlt = node.props.imageAlt ?? "";
    const columnHref = node.props.imageHref;
    return {
      tagName: "mj-column",
      attributes: compact({
        width: node.props.width,
        "vertical-align": node.props.verticalAlign,
        padding: "0px"
      }),
      children: [
        {
          tagName: "mj-image",
          attributes: compact({
            src: rasterizedColumn.url,
            width: `${rasterizedColumn.width}px`,
            alt: columnAlt,
            href: columnHref,
            padding: "0px"
          })
        }
      ]
    };
  }
  const children = (await Promise.all(node.children.map((child) => compileContentNode(child, ctx, emailWidth, brand, state)))).flat();
  const bg = asString(node.style?.backgroundColor);
  const radiusValue = borderRadius(node.style?.borderRadius);
  const borderAttrs = border(node.style?.border);
  const hasGutter = node.style?.padding !== void 0;
  const styleAttrs = hasGutter ? compact({
    "inner-background-color": bg,
    "inner-border-radius": radiusValue,
    "inner-border": borderAttrs.border,
    "inner-border-top": borderAttrs["border-top"],
    "inner-border-right": borderAttrs["border-right"],
    "inner-border-bottom": borderAttrs["border-bottom"],
    "inner-border-left": borderAttrs["border-left"]
  }) : compact({
    "background-color": bg,
    "border-radius": radiusValue,
    ...borderAttrs
  });
  const mobilePaddingCss = spacing(node.style?.mobile?.padding);
  let cssClass;
  if (mobilePaddingCss) {
    state.mobileColumnPaddings.add(mobilePaddingCss);
    cssClass = mobileColumnPaddingClass(mobilePaddingCss);
  }
  return {
    tagName: "mj-column",
    attributes: compact({
      width: node.props.width,
      "vertical-align": node.props.verticalAlign,
      padding: spacing(node.style?.padding),
      "css-class": joinClasses("nm-col", `nm-id-${node.id}`, cssClass),
      ...styleAttrs
    }),
    children
  };
}
async function compileContentNode(node, ctx, emailWidth, brand, state) {
  switch (node.type) {
    case "text": {
      const content = node.props.format === "html" ? node.props.content : wrapSemanticText(escapeHtml(node.props.content), node.props.role);
      const mobileAlign = node.style?.mobile?.align;
      if (mobileAlign) state.mobileTextAligns.add(mobileAlign);
      return [
        {
          tagName: "mj-text",
          attributes: compact({
            "font-size": px(node.style?.fontSize),
            "font-weight": asString(node.style?.fontWeight),
            color: asString(node.style?.color),
            align: node.style?.align,
            "line-height": lineHeight(node.style?.lineHeight),
            "letter-spacing": px(node.style?.letterSpacing),
            "font-family": asString(node.style?.fontFamily),
            padding: spacing(node.style?.padding),
            "css-class": joinClasses(
              "nm-blk",
              "nm-blk-text",
              `nm-id-${node.id}`,
              mobileAlign ? `nm-m-align-${mobileAlign}` : void 0
            )
          }),
          content
        }
      ];
    }
    case "image": {
      const resolved = await resolveImage(node, ctx, brand);
      if (resolved.kind === "brand-logotype") {
        return [
          {
            tagName: "mj-text",
            attributes: compact({
              "css-class": joinClasses("nm-blk", "nm-blk-text", `nm-id-${node.id}`),
              align: node.style?.align ?? "center",
              color: ctx.theme.colors["text.body"] ?? "#111827",
              "font-size": "22px",
              "font-weight": "700",
              "letter-spacing": "0.02em",
              padding: spacing(node.style?.padding)
            }),
            content: escapeHtml(resolved.label)
          }
        ];
      }
      const src = resolved.src;
      const isGif = isGifUrl(src);
      const hasExplicitHeight = typeof node.style?.height === "number";
      const effectiveFit = !isGif ? node.style?.fit ?? (hasExplicitHeight ? "cover" : void 0) : void 0;
      if (effectiveFit && src) {
        const targetWidth = typeof node.style?.width === "number" ? node.style.width : emailWidth;
        const targetHeight = node.style?.height ?? heightFromAspectRatio(targetWidth, node.style?.aspectRatio ?? "4:3");
        try {
          const fitted = await ctx.resolveFittedImage({
            imageUrl: src,
            width: targetWidth,
            height: targetHeight,
            mode: effectiveFit
          });
          return [
            {
              tagName: "mj-image",
              attributes: compact({
                "css-class": joinClasses("nm-blk", "nm-blk-image", `nm-id-${node.id}`),
                src: fitted.url,
                alt: node.props.alt,
                href: node.props.href,
                width: px(fitted.width),
                align: node.style?.align,
                "border-radius": borderRadius(node.style?.borderRadius),
                padding: spacing(node.style?.padding),
                ...border(node.style?.border)
              })
            }
          ];
        } catch (err) {
          ctx.logger.warn(`fit failed for ${src}: ${err.message}`);
        }
      }
      return [
        {
          tagName: "mj-image",
          attributes: compact({
            src,
            alt: node.props.alt,
            href: node.props.href,
            width: typeof node.style?.width === "number" ? px(node.style.width) : void 0,
            // height intentionally omitted — only set alongside width via the
            // fit pipeline above. Setting height here without fit would
            // stretch the image at any column width other than the declared one.
            align: node.style?.align,
            "border-radius": borderRadius(node.style?.borderRadius),
            padding: spacing(node.style?.padding),
            // mjml-validator's Boolean type requires a raw boolean (=== true)
            // and rejects the string "true", which is all the JSON-attribute
            // path can carry. Bypass it by emitting the class fluid-on-mobile
            // would have added internally (see mjml-image source).
            "css-class": joinClasses(
              "nm-blk",
              "nm-blk-image",
              `nm-id-${node.id}`,
              node.style?.fluidOnMobile ? "mj-full-width-mobile" : void 0
            ),
            ...border(node.style?.border)
          })
        }
      ];
    }
    case "button":
      return [
        {
          tagName: "mj-button",
          attributes: compact({
            "css-class": joinClasses("nm-blk", "nm-blk-button", `nm-id-${node.id}`),
            href: node.props.href,
            "background-color": asString(node.style?.backgroundColor),
            color: asString(node.style?.color),
            "border-radius": borderRadius(node.style?.borderRadius),
            "font-size": px(node.style?.fontSize),
            "font-weight": asString(node.style?.fontWeight),
            padding: spacing(node.style?.padding),
            align: node.style?.align,
            width: node.style?.fullWidth ? "100%" : void 0,
            ...border(node.style?.border)
          }),
          content: escapeHtml(node.props.label)
        }
      ];
    case "divider":
      return [
        {
          tagName: "mj-divider",
          attributes: compact({
            "css-class": joinClasses("nm-blk", "nm-blk-divider", `nm-id-${node.id}`),
            "border-color": asString(node.style?.color),
            "border-width": px(node.style?.thickness ?? 2),
            width: node.style?.width,
            padding: spacing(node.style?.padding)
          })
        }
      ];
    case "spacer":
      return [{ tagName: "mj-spacer", attributes: compact({ height: px(node.props.height) }) }];
    case "social": {
      state.hasSocialIcons = true;
      const brandIcons = ctx.brand.socialIcons;
      const cdnBase = ctx.socialIconCdnBase;
      return [
        {
          tagName: "mj-social",
          attributes: compact({
            align: node.style?.align,
            "icon-size": px(node.style?.iconSize),
            padding: spacing(node.style?.padding)
          }),
          children: node.props.links.map((link) => ({
            tagName: "mj-social-element",
            attributes: compact({
              src: link.iconUrl || brandIcons?.[link.platform] || socialIconUrl(link.platform, cdnBase),
              href: link.href,
              "background-color": "transparent",
              "border-radius": "0",
              "css-class": "nm-social-icon"
            })
          }))
        }
      ];
    }
    case "navbar":
      return [
        {
          tagName: "mj-navbar",
          attributes: compact({
            align: node.style?.align,
            hamburger: node.props.hamburger ? "hamburger" : void 0,
            padding: spacing(node.style?.padding)
          }),
          children: node.props.links.map((link) => ({
            tagName: "mj-navbar-link",
            attributes: compact({
              href: link.href,
              color: asString(node.style?.color),
              "font-size": px(node.style?.fontSize),
              "font-family": asString(node.style?.fontFamily),
              padding: spacing(node.style?.linkPadding),
              "letter-spacing": typeof node.style?.letterSpacing === "number" ? `${node.style.letterSpacing}px` : void 0,
              "text-transform": node.style?.textTransform
            }),
            content: escapeHtml(link.label)
          }))
        }
      ];
    default:
      throw new Error(`Unsupported content node type: ${node.type}`);
  }
}

// src/email-builder/compiler/theme.ts
function createDefaultTheme(brand) {
  return {
    colors: {
      "surface.primary": brand?.secondaryColor ?? "#FFFFFF",
      accent: brand?.accentColor ?? brand?.primaryColor ?? "#111827",
      // Derive `text` (the canonical key templates use) and `text.body`
      // (legacy) from the same source. `text.muted` is purely a default —
      // brands rarely set it.
      text: brand?.textColor ?? "#111827",
      "text.body": brand?.textColor ?? "#111827",
      "text.muted": "#6B7280",
      primary: brand?.primaryColor ?? "#111827",
      secondary: brand?.secondaryColor ?? "#FFFFFF",
      background: brand?.backgroundColor ?? "#FFFFFF",
      ...brand?.colorsByUsage ?? {}
    },
    typography: {
      families: {
        heading: brand?.headingFont ?? "Helvetica, Arial, sans-serif",
        body: brand?.bodyFont ?? "Helvetica, Arial, sans-serif",
        mono: "Menlo, Monaco, monospace",
        ...brand?.fontsByUsage ?? {}
      },
      sizes: { h1: 32, h2: 24, body: 16, caption: 12 },
      weights: { regular: 400, medium: 500, bold: 700 }
    },
    spacing: { xs: 4, sm: 8, md: 16, lg: 24, xl: 32, xxl: 40 },
    radius: { none: 0, sm: 4, md: 8, lg: 16, pill: 9999 },
    buttons: {
      primary: {
        // Prefer the dedicated `button` color (extracted by the brand
        // scraper from CTA buttons on the brand site) over the more
        // generic `primaryColor`. Brands often have a button color that
        // differs from their dominant primary color (e.g. black CTAs on
        // a teal brand). Falls through to `primaryColor` for back-compat.
        backgroundColor: brand?.colorsByUsage?.button ?? brand?.primaryColor ?? "#111827",
        color: "#FFFFFF",
        borderRadius: 8,
        padding: { top: 12, right: 24, bottom: 12, left: 24 },
        fontWeight: 700,
        fontSize: 16
      },
      secondary: {
        backgroundColor: brand?.colorsByUsage?.buttonSecondary ?? brand?.secondaryColor ?? "#FFFFFF",
        color: brand?.colorsByUsage?.buttonSecondary ?? brand?.primaryColor ?? "#111827",
        borderRadius: 8,
        padding: { top: 12, right: 24, bottom: 12, left: 24 },
        fontWeight: 700,
        fontSize: 16
      },
      ghost: {
        backgroundColor: "transparent",
        color: brand?.primaryColor ?? "#111827",
        borderRadius: 8,
        padding: { top: 12, right: 16, bottom: 12, left: 16 },
        fontWeight: 700,
        fontSize: 16
      }
    },
    surfaces: {
      primary: {
        backgroundColor: brand?.secondaryColor ?? "#FFFFFF",
        textColor: brand?.textColor ?? "#111827"
      }
    }
  };
}
export {
  ALLOWED_CHILDREN,
  BLANK_PRODUCT_ID,
  BODY_BAND_ID,
  BRAND_MERGE_TAG_PATHS,
  CANVAS_OBJECT_TYPES,
  CheckerRegistry,
  MERGE_TAG_CATEGORIES,
  MINIMAL_COMPLIANCE_FOOTER_HTML,
  MergeTag,
  PRESET_CATALOG,
  SECTION_PRESETS,
  SECTION_ROLE_CLASSES,
  SOCIAL_ICONS,
  analyze,
  analyzeLayout,
  applyChatStyleOverrides,
  asString,
  bodyBandOf,
  border,
  borderRadius,
  borderSide,
  brandContextFromCompileCtx,
  buildAnalyzerContext,
  buildPreviewSlotDefaults,
  buildProductApplyPatches,
  buildRasterizePlan,
  classifyDecorationEdge,
  classifyNmTag,
  collectInteractiveDescendants,
  collectProductFieldNodes,
  compact,
  compileCanvasSection,
  compileCanvasSectionNode,
  compileChatTemplate,
  compileEmailDocument,
  compileLiveFrame,
  compileProductFeed,
  compileRootCanvasObject,
  computeColumnWidths,
  computeGroupMinHeight,
  containsMergeTags,
  containsTemplateDelimiters,
  contrastingText,
  createCanvasObject,
  createDefaultTheme,
  createEmptyEmailDocument,
  createFooterSubtree,
  createHeaderSubtree,
  createNode,
  createSectionSubtree,
  deepCloneWithNewIds,
  defaultRegistry,
  describeLiveIneligibilityReason,
  escapeHtml,
  extractBandRunAsDocument,
  extractFrameAsDocument,
  extractSlotGroups,
  extractSlots,
  filterPresets,
  findAncestorChain,
  findDescendants,
  findNmTags,
  findNode,
  findOutermostRasterizeNodes,
  findParent,
  findPreset,
  findProductBlockRoot,
  formatProductPrice,
  getBodyChildren,
  getChildren,
  getLiveIneligibilityReasons,
  getNodeChildren,
  getSectionFrames,
  getSectionHtmlRef,
  getSectionSendSummary,
  gradientFirstStop,
  gradientToCss,
  groupByNodeId,
  hasMergeTag,
  hasProductFeed,
  heightFromAspectRatio,
  hexToRgba2 as hexToRgba,
  highestSeverity,
  isBrandMergeTag,
  isCanvasFirstEmail,
  isChatInsertable,
  isContainerNode,
  isGifUrl,
  isKnownSocialPlatform,
  isLiveEligible,
  isRasterizeCandidate,
  isRecipientFieldProperty,
  isSectionNode,
  isSectionTarget,
  isSendTimeMergeTag,
  isSlotMergeTag,
  isTextHeavySection,
  joinClasses,
  lineHeight,
  mediaOffsetForId,
  mergeBrandFontsIntoChatHtml,
  mjmlNodeToString,
  mobileColumnPaddingClass,
  nmToken,
  normalizeClickMapToBands,
  overlayToCss,
  parseRecipientFieldTag,
  parseSlotTag,
  px,
  rasterizeContainsMergeTagsChecker,
  rasterizeLinkConflictChecker,
  rasterizeMissingAltTextChecker,
  rasterizeMissingHrefChecker,
  readProductSnapshot,
  resolveDefaultSectionRefs,
  resolveImage,
  resolveMergeTags,
  resolveMergeTagsInTree,
  resolveSectionCompileMode,
  resolveSectionForInsert,
  sectionRoleClass,
  sectionRoleOf,
  sectionRowToChatHtml,
  socialIconUrl,
  softenHex,
  spacing,
  stampProductCutoutIds,
  stockImageForSlot,
  walkNodes,
  walkTree,
  wrapSemanticText
};
